/** CrmRecordPanel (Item D) — a single-record overlay rendered OVER the list grid.
 *
 * PRESENTATIONAL. It renders a record's field grid and (once hydrated) its actionable field state.
 * All data + mutations are owned by CrmRecordPanelContainer, which passes the authoritative
 * `record_field_states.v1` payload down as props. This split keeps the panel free of hooks (so the D1
 * presentational tests stay trivial) and makes the hydrate-before-actionable invariant explicit:
 *
 *   - **No `fieldStates` prop → read-only** (spec §5.4 / R3 / §6.5): while the record endpoint is in
 *     flight the panel paints instantly from the loaded grid-row snapshot, with NO edit / accept /
 *     dismiss affordances. Actionable controls appear only once the container passes `fieldStates`
 *     (i.e. the authoritative payload has arrived).
 *   - **`fieldStates` present → actionable** (D2b): per-type editors → `useSetFieldValue`; compact
 *     one-line proposal redlines with hover Accept ✓ / Dismiss ✗ (`expected_proposal_id` staleness);
 *     overlay pending = blue dot; push-failed = persistent red error + Retry. Status is NEVER derived
 *     here — it comes verbatim from the payload's `field_states[].status` (spec §5.4-1).
 */
import {
  createContext, Fragment, useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react';
import type { CustomCellEditorProps } from 'ag-grid-react';
import { CrmFieldHistory } from './CrmFieldHistory';
import { CrmFactEditModal } from './CrmFactEditModal';
import { FactWhyButton } from './CrmFactEvidencePopover';
import styles from './crm.module.css';
import type {
  CrmFactInterpretResponse, CrmGridColumn, CrmProjectedAddOptions, CrmProjectedChip,
  CrmProjectedFact, CrmRecordFieldState, CrmRecordLayout, CrmRefValue, CrmRosterOption,
  CrmStageLadders, RecordReviewV2Field,
} from '../../types';
import { CrmBlockRuns } from './CrmBlockRuns';
import { isResolvedCommittedMember } from './recordReviewV2';
import { editedBlockValue, groupBlockItems } from './crmBlockGroups';
import { cellText, cellValues } from './crmCells';
// plan 1b: the deal_type-contingent Stage ladder — the SAME module the grid filters through.
import { stageLadderOptions } from './stageLadder';
import { isProposedRecordRow } from './crmAiProposedRow';
import { canOpenProfile, EntityRefs, refLabel, refLocalId, visibleRefs } from './EntityRefs';
import {
  CATCHALL_FIELD_KEYS, CATCHALL_ORGS_FIELD_KEY, CATCHALL_PEOPLE_FIELD_KEY, contradictionKey,
  contradictionSummary, findSideContradictions, flowsInColumns, formatRole, groupHeading,
  isMeaningfulRole, partyHeading, personSubtitle, profileHref, rolePartyPairs,
  type PartyRef, type SideContradiction,
} from './crmEntityRoles';
import { CrmPartiesField } from './CrmPartiesField';
import { PARTIES_FIELD_KEY, PARTY_TREE_FIELD_TYPE } from './crmPartiesSort';
import { fmtRelativeDay, fmtShortDate } from './crmFormat';
import { panelFieldEditor, type PanelEditorKind } from './crmEditability';
import {
  OPEN_AI_UPDATE_COUNT_FIELD, SYSTEM_NULL_DISPLAY, enumDisplay, isComputedColumn,
  systemCellTooltip, systemCoverageFor,
} from './crmSystemFields';
import { CrmChecklistEditor } from './CrmChecklistEditor';
import { RefCellEditor } from './CrmRefCellEditor';
import { CrmRosterPicker } from './CrmRosterPicker';

const REF_TYPES = new Set(['person', 'person_multi', 'organization', 'organization_multi']);

// ── owner item 2 (2026-08-09): the type + qualifier keys the hover card treats specially ──────────
/** The relationship that carries `deal_party_role` — who the entity IS on the deal. */
const PARTICIPATION_TYPE = 'deal_participation';
/** Target / seller / buyer / lender — participation's own answer, and NOT expressible anywhere else. */
const PARTY_ROLE_QUALIFIER_KEY = 'deal_party_role';
/** The side, rendered through `partyHeading` so `buy` reads "Buy side" — the desk's own phrasing. */
const SIDE_QUALIFIER_KEY = 'transaction_side';
/** Free text, so it is quoted rather than word-cased, and it reads LAST among the qualifiers. */
const DESCRIPTOR_QUALIFIER_KEY = 'role_descriptor';

/** "who said this" — the first question asked about a fact you disagree with. */
function factProvenance(fct: CrmProjectedFact): string {
  if (fct.producer_kind === 'manual') return 'entered by a person';
  return fct.human_decided ? 'from the AI, confirmed' : 'from the AI';
}

/**
 * A `deal_participation` fact that says NOTHING — the line the owner asked to stop seeing.
 *
 * ⚠ **The test is emptiness, NOT "some other type also exists", and the difference is data loss.**
 * The first cut of this suppressed every participation fact whenever a specific one was present, on
 * the reasoning that a specific type implies participation. It does not imply its CONTENT:
 * `deal_participation` is the only relationship carrying `deal_party_role` (target / seller / buyer /
 * lender), and the register deliberately gives it no side because the role already implies one —
 * while `transaction_advisor` states a discipline and a side and says nothing whatever about being
 * the target. One entity holding both is an ordinary recorded shape (`party_reconcile.py`), so the
 * old predicate deleted "Target" and its descriptor from the card of the very entity that is the
 * target — and in a `Target` group, hid the fact that created the group. The same held for
 * `deal_correspondent` (the envelope floor, which carries no identity-bearing qualifiers at all):
 * once that flag flips it would have counted as "specific" and suppressed a manual seller.
 *
 * What the owner actually reported is E5a's legacy conversion, which asserts participation with
 * `deal_party_role='unclassified'` and nothing else — a line whose every segment is the absence of
 * information. That, and only that, is dropped: no meaningful role AND no free-text descriptor.
 */
function isVacuousParticipation(fct: CrmProjectedFact): boolean {
  if (fct.relationship_type !== PARTICIPATION_TYPE) return false;
  const quals = fct.qualifiers ?? {};
  return !isMeaningfulRole(quals[PARTY_ROLE_QUALIFIER_KEY])
    && (quals[DESCRIPTOR_QUALIFIER_KEY] ?? '').trim() === '';
}

// Value-equality for the review redline: a no-op proposal (proposed == current — e.g. the aggregator
// re-proposing an unchanged value) must NOT render the value twice (proposed chip + struck-prior chip).
// Arrays (multi-enum / entity_multi) compared order-insensitively; scalars by normalized JSON.
function sameCellValue(a: unknown, b: unknown): boolean {
  const norm = (v: unknown): string =>
    Array.isArray(v) ? v.map((x) => JSON.stringify(x)).sort().join('') : JSON.stringify(v ?? null);
  return norm(a) === norm(b);
}

// Mirrors CrmListGrid's Slice-10 pill color mapping. Duplicated here (small + pure) to avoid a
// circular import — CrmListGrid imports this panel. Folded into a shared util in the D-integration
// cleanup (tracked in the D1 review).
const PILL_CLASS: Record<string, string> = {
  green: styles.pillGreen, blue: styles.pillBlue, grey: styles.pillGrey,
  orange: styles.pillOrange, red: styles.pillRed, purple: styles.pillPurple,
};
function colorMapFromGroups(groups: CrmGridColumn['groups']): Record<string, string> {
  const map: Record<string, string> = {};
  for (const grp of groups ?? []) for (const o of grp.options ?? []) if (o.color) map[o.value.toLowerCase()] = o.color;
  return map;
}

// S5b: a Progress / New-Info block value is an ARRAY OF BULLETS — `[{item_id, text,
// evidence_event_ids, group, materiality}]` (S5a-2 shape + the S2 annotations; a legacy row may hold
// bare strings). It must never reach `cellText`, which stringifies a dict to "[object Object]" — that
// is exactly why the `panel` surface stayed closed until this renderer existed (they had to ship
// together). Text + grouping are projected by the shared `crmBlockGroups` helper.
const BLOCK_FIELD_TYPE = 'qualitative_block';

// S3a — the §9 `next_steps` bullet convention (bounded-emission design, owner-ruled 2026-08-09).
// `next_steps` is plain `long_text` (replaced current state, not an accumulating log), so it carries
// NO item model. The aggregator writes 3+ discrete actions as newline-separated "- " lines and 1-2
// items as prose, and the panel reads the shape off the text alone.
//
// Key-scoped on purpose: every OTHER long_text (opportunity_description, notes, …) renders exactly as
// it did before, even if it happens to contain a dashed line. Widening the convention to another
// field is an edit to this set.
const BULLET_CONVENTION_FIELD_KEYS = new Set(['next_steps']);

// Mirrors backend `text_utils.next_steps_bullet_lines` — ONE rule, stated twice because the note and
// the notify email are Python. Keep the two in step. Strict by design: at least two lines and EVERY
// non-empty line dashed, so a prose lead-in followed by dashes stays prose rather than being
// half-converted into a list.
//
// ⚠ The line grammar and the trim set are pinned CHARACTER-FOR-CHARACTER against the Python side,
// because the two languages disagree by default and the disagreement is invisible: Python's
// `splitlines()` breaks on U+2028/U+2029/NEL/VT/FF/FS where `split('\n')` does not, and Python's
// `.strip()` removes NEL where JS `.trim()` does not. Either gap makes the SAME value render as
// bullets in the note and the email but as prose here. Split on CR?LF only; trim exactly ASCII
// whitespace + NBSP; everything exotic falls back to prose on BOTH sides.
const NS_LINE_SPLIT = /\r?\n/;
const NS_TRIM = /^[ \t\v\f\r\n\u00a0]+|[ \t\v\f\r\n\u00a0]+$/g;
const nsTrim = (s: string) => s.replace(NS_TRIM, '');

function bulletConventionLines(value: unknown): string[] | null {
  if (typeof value !== 'string') return null;
  const lines = value.split(NS_LINE_SPLIT).map(nsTrim).filter((l) => l !== '');
  if (lines.length < 2 || !lines.every((l) => l.startsWith('- '))) return null;
  // slice(2) strips the "- " prefix EXACTLY ONCE: "- -5% EBITDA guidance" is one action about a
  // negative number, not a nested bullet, and must survive as "-5% EBITDA guidance".
  const items = lines.map((l) => nsTrim(l.slice(2))).filter((l) => l !== '');
  return items.length > 0 ? items : null;
}

/** Bullet list for a qualitative block. `tone` picks the redline colour: 'new' = an active proposal
 * (green), 'plain' = settled/current.
 *
 * S2/S3 grouped summaries (bounded-emission design §4.3): the items may carry a free-text `group`,
 * and when any of them does this renders one sub-header per group via the SHARED `groupBlockItems` —
 * the same runs `CrmRecentUpdates` paints, because a grouped item rendering grouped in the
 * Recent-Updates cluster and flat here is one record contradicting itself on one screen. Both tones
 * group: a proposal is reviewed on exactly the shape it will become once accepted.
 *
 * The runs themselves are painted by the shared `CrmBlockRuns`, which also owns the pre-S2
 * compatibility path (an all-ungrouped block renders the BARE `<ul>` this always rendered, no
 * wrapper). This function owns only the EMPTY state, which differs per surface. */
function BlockValue({ value, tone }: { value: unknown; tone: 'new' | 'plain' }) {
  const groups = groupBlockItems(value);
  if (groups.length === 0) return <span className={styles.gridEmpty}>—</span>;
  return (
    <CrmBlockRuns groups={groups} tone={tone}
                  listClassName={tone === 'new' ? styles.blockBulletsNew : styles.blockBullets} />
  );
}

/**
 * P3 — the org↔people side disagreements on THIS record, keyed by `contradictionKey`.
 *
 * A context rather than a prop because the derivation reads ACROSS two fields (`all_people` needs
 * `all_orgs` and vice versa) while both renderers are handed one column at a time — and because
 * `renderValue` is a plain function shared by eight call sites that have no business knowing about
 * this. Computed once in `CrmRecordPanel`, so the pre-hydrate and hydrated renderers badge from the
 * same map and the ⚠ cannot appear, vanish and reappear as the record endpoint answers.
 *
 * Empty by default: every other surface that mounts these renderers (and every existing test) gets
 * exactly today's output.
 */
const EMPTY_CONTRADICTIONS: ReadonlyMap<string, SideContradiction[]> = new Map();
const PartyContradictionsContext =
  createContext<ReadonlyMap<string, SideContradiction[]>>(EMPTY_CONTRADICTIONS);

/**
 * P3 — the ⚠ that says these two rows cannot both be right, with the disagreement spelled out on
 * hover.
 *
 * Reuses `.factHoverAnchor` / `.factHoverCard` — the same CSS-only, `:focus-within`-reachable,
 * `pointer-events:none` card the full-detail hover uses, so it can never swallow the click meant for
 * the pencil beneath it. The badge itself carries the whole sentence as its accessible name: a bare
 * "⚠" announces nothing, and the hover card is decoration to a screen reader.
 */
function ContradictionBadge({ entries }: { entries: readonly SideContradiction[] }) {
  if (entries.length === 0) return null;
  const lines = entries.map(contradictionSummary);
  return (
    <span className={styles.factHoverAnchor}>
      <span className={styles.contradictionBadge} role="img"
            aria-label={`Side disagreement. ${lines.join(' ')}`}>⚠</span>
      <span className={styles.factHoverCard} role="tooltip">
        {lines.map((line, i) => (
          <span key={i} className={styles.factHoverRow}>{line}</span>
        ))}
      </span>
    </span>
  );
}

/**
 * Group order inside a catch-all field: alphabetical by display label, **and the headless group LAST**.
 *
 * ⚠ **Headless-last is deliberate and it preserves where the pile already sits.** The unlabelled group
 * is the ~387 legacy refs E5a converted as `unclassified` and nobody has classified since — the
 * biggest group on many deals and the least informative. Before cleanup PR 2 it carried the literal
 * role `unclassified`, which sorted alphabetically and so landed near the bottom; dropping the label
 * (owner item 1) would have moved that pile to the TOP of every field, so the noise-reduction ask
 * would have made the field OPEN on the noise. Sorting it last keeps the operator's eye on the
 * answered groups and reads the unanswered ones as the remainder, which is what they are.
 *
 * Shared by both grouping renderers, for the same reason the grouping itself is: a difference between
 * them is visible as the field re-laying itself out a moment after opening.
 */
function compareRoleGroups(
  a: { role: string | null; party: string | null }, b: { role: string | null; party: string | null },
): number {
  if (a.role === null || b.role === null) {
    return a.role === b.role ? 0 : a.role === null ? 1 : -1;
  }
  return formatRole(a.role).localeCompare(formatRole(b.role))
    || (a.party ?? '').localeCompare(b.party ?? '');
}

/** The full line one member renders as — `Name` or `Name, Title, Company`. Measured, not displayed. */
function partyLineText(label: string | null, ref: CrmRefValue): string {
  const subtitle = personSubtitle(ref);
  const name = label ?? '';
  return subtitle ? `${name}, ${subtitle}` : name;
}

/** P1 — a group of short bare labels flows into columns; anything longer stays one per line. */
function memberColumnsClass(lines: string[]): string {
  return flowsInColumns(lines) ? styles.roleGroupMembersColumns : styles.roleGroupMembers;
}

/**
 * §15 item 2 — the catch-all entity columns, GROUPED BY ROLE, one entity per row, each opening its
 * profile in a new tab.
 *
 * The owner's shape: `[Org Name]` under a role heading, and `[Name], [Title], [Org]` for a person.
 *
 * Title and employer ARE included as of the owner's 2026-08-05 ruling that they are firm-wide directory
 * information. They come from `entity_links._attach_employment` — a governed read, not the stored ref —
 * and the employer is present only when that ORG itself passed the gate, so a restricted or merged-away
 * employer is absent rather than named. Nothing here can invent one; `personSubtitle` skips whatever is
 * missing so a person with no employment on record renders as a plain name.
 *
 * An entity carrying several roles appears under EACH of them. That is not a duplication bug: the
 * question "who are the lawyers on this deal" must list a firm that is also the target, and hiding it
 * from one group to keep the list short is the half-truth the flat cell already had.
 */
function RoleGroupedRefs({ value, kind }: { value: unknown; kind: 'person' | 'organization' }) {
  const contradictions = useContext(PartyContradictionsContext);
  const refs = visibleRefs(value);
  if (refs.length === 0) return <span className={styles.gridEmpty}>—</span>;

  // `null` IS the unlabelled group — no sentinel string. An in-band sentinel needs a magic value that
  // must differ from every real slug, and the first cut of this used a control character that git then
  // read as a binary file, hiding ~900 lines of this file from every diff. A nullable key cannot
  // collide and cannot be mistyped.
  // §1.5(a) — TWO levels now: the role, then the party within it. Keyed on `role|party` so a firm
  // advising both sides of one deal in one discipline appears under each side, which is the whole ask
  // ("buyside banker vs sellside"). A role with no party contributes ONE bucket with `party: null`,
  // which renders flat — §1.5(a) forbids an "Unknown" heading for a question never askable.
  const groups = new Map<string, { role: string | null; party: string | null; refs: CrmRefValue[] }>();
  for (const r of refs) {
    for (const [role, parties] of rolePartyPairs(r)) {
      for (const party of parties) {
        const key = `${role ?? ''}|${party ?? ''}`;
        const bucket = groups.get(key);
        if (bucket) bucket.refs.push(r);
        else groups.set(key, { role, party, refs: [r] });
      }
    }
  }
  // Alphabetical by DISPLAY label so the order matches what the operator reads. Stable across
  // renders — a section list that reorders reads as data changing.
  const ordered = [...groups.values()].sort(compareRoleGroups);

  return (
    <div className={styles.roleGroups}>
      {ordered.map(({ role, party, refs: members }) => (
        <div key={`${role ?? '—unlabelled'}|${party ?? ''}`} className={styles.roleGroup}>
          {/* P1 — ONE heading line, role and side together. Two stacked headings repeated the role
              above every side of it, which is most of what made these fields run long. */}
          {groupHeading(role, party) && (
            <div className={styles.roleGroupLabel}>{groupHeading(role, party)}</div>
          )}
          <div className={memberColumnsClass(members.map((r) => partyLineText(refLabel(r), r)))}>
            {members.map((r, i) => {
              // §3.3 — an internal person's name renders as plain text, not a link that 404s. The
              // member itself still renders: role groups are names, not profiles (§3.4).
              const id = canOpenProfile(r) ? refLocalId(r, kind) : null;
              const label = refLabel(r) as string;
              const subtitle = personSubtitle(r);
              const flags = (typeof r.local_id === 'number'
                ? contradictions.get(contradictionKey(kind, r.local_id)) : undefined) ?? [];
              return (
                <div key={`${role ?? ''}-${party ?? ''}-${i}`} className={styles.roleGroupRow}>
                  {id != null ? (
                    <a className={styles.gridRefLink} href={profileHref(kind, id)}
                       target="_blank" rel="noopener noreferrer">{label}</a>
                  ) : (
                    <span className={styles.gridRef}>{label}</span>
                  )}
                  {/* P1 — `Name, Title, Org` on ONE line. The comma is a real text node rather than a
                      CSS `::before`, so the line copies and reads aloud the way it looks. Absent parts
                      are still skipped, so a person with no employment reads as a plain name. */}
                  {subtitle && <>{', '}<span className={styles.roleGroupSubtitle}>{subtitle}</span></>}
                  <ContradictionBadge entries={flags} />
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * How a value is toned: an active AI proposal, the settled current value, or the struck PRIOR value
 * beside a proposal.
 *
 * ⚠ **`'prior'` exists because a pill does not inherit a tone** (§15 item 3). The redline wrapper spans
 * set `color` + `text-decoration`, which reads correctly on text but is invisible on an enum pill — the
 * pill paints its own background and colour from the option colour map. So a stage proposal and its
 * struck prior rendered as two identical tiles, which is what "duplicate stage tiles" was. Two tones
 * could not express it: the rest state and the struck prior are BOTH "not a proposal" but must not look
 * alike.
 */
type ValueTone = 'new' | 'plain' | 'prior';

/**
 * §7.4's definition/freshness tooltip for a system cell — **the panel's half of W5's blank contract**.
 *
 * ⚠ **The owner ruled system fields into the record panel (2026-08-30), and the panel had none of
 * this.** A blank system cell rendered a bare em dash with NO title at all, while the grid's identical
 * cell said "Not calculated yet — this is not a statement about the record." (or "No such date is
 * known."). At 12.8% activity coverage and with `crm_record_system_rollup` holding zero rows in prod,
 * that is most system cells on the surface the ruling added — and "this record has no such date" is
 * precisely the inference S12 exists to prevent.
 *
 * Reuses the grid's own `systemCellTooltip` + `systemCoverageFor`, so the two surfaces cannot drift:
 * one definition of what a blank MEANS, read from the same `_system_activity_state` /
 * `_system_record_state` the row already carries. Returns undefined for a non-system column so the
 * caller's existing title (or absence of one) is untouched.
 */
function systemTitle(
  col: CrmGridColumn,
  record: Record<string, unknown> | null | undefined,
  value: unknown,
): string | undefined {
  if (!col.system_family) return undefined;
  const isBlank = value == null || value === ''
    || (col.field_key === OPEN_AI_UPDATE_COUNT_FIELD && !(Number(value) > 0));
  const text = systemCellTooltip(col, {
    exact: !isBlank && col.field_type === 'date' ? fmtShortDate(value as string) : null,
    ...systemCoverageFor(record ?? undefined, col, fmtShortDate),
    isBlank,
  });
  return text || undefined;
}

/** Read-only value renderer mirroring the grid's cell vocabulary (chips / colored pills / relative
 * dates / currency). Shared by the rest-state value, the proposal redline's old + new values, and the
 * read-only fallback. */
function renderValue(col: CrmGridColumn, value: unknown, tone: ValueTone = 'plain'): ReactNode {
  // Blocks first: the emptiness test below treats `[]` as empty, which is right for a block too, but a
  // block's non-empty rendering must be a bullet list rather than any of the scalar/chip vocabularies.
  // A block is ADDITIVE, so it never renders a struck prior beside a proposal (see .blockBullets) —
  // 'prior' therefore collapses to 'plain' rather than needing a third bullet style.
  if (col.field_type === BLOCK_FIELD_TYPE) {
    return <BlockValue value={value} tone={tone === 'new' ? 'new' : 'plain'} />;
  }
  if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
    return <span className={styles.gridEmpty}>{SYSTEM_NULL_DISPLAY}</span>;
  }
  // ⚠ **`0` is a BLANK for the AI update count, and the emptiness guard above does not catch it.**
  // The grid's `PendingCountRenderer` returns an em dash for `n <= 0` — "a clickable zero would teach
  // people the number is a button rather than a fact" — so the panel printed `0` on exactly the rows
  // where the grid beside it printed `—`. Same rule, same constant, one field: this is the count's
  // own presentation rule, not a general "numbers render blank at zero".
  if (col.field_key === OPEN_AI_UPDATE_COUNT_FIELD) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) {
      return <span className={styles.gridEmpty}>{SYSTEM_NULL_DISPLAY}</span>;
    }
  }
  // S3a: a `next_steps` value written to the §9 convention renders as a real list, reusing the block
  // renderer's bullet styling so the narrative cluster reads as one thing. 'prior' is excluded
  // deliberately — the struck-prior sub-line gets its colour + strikethrough from the redline
  // wrapper, which a <ul> with its own colour would override (the same trap §15 item 3 documents for
  // enum pills). A struck prior stays flat text.
  if (BULLET_CONVENTION_FIELD_KEYS.has(col.field_key) && tone !== 'prior') {
    const steps = bulletConventionLines(value);
    if (steps) {
      return (
        <ul className={tone === 'new' ? styles.blockBulletsNew : styles.blockBullets}>
          {steps.map((t, i) => <li key={i}>{t}</li>)}
        </ul>
      );
    }
  }
  const ft = col.field_type;
  // Party-facts lever D: the `parties` tree is a REF-BEARING value that is not a ref CELL — it is a
  // nested `{groups: [...]}` structure, and `REF_TYPES` does not (and must not) contain it, so it is
  // routed first. `CrmPartiesField` renders nothing unless the payload carries the per-viewer
  // enrichment marker, which is what keeps an un-enriched grid row from being drawn with zero counts.
  if (ft === PARTY_TREE_FIELD_TYPE) {
    return <CrmPartiesField value={value} />;
  }
  if (REF_TYPES.has(ft)) {
    const kind = ft.startsWith('person') ? 'person' : 'organization';
    // §15 item 2: the catch-all columns render GROUPED BY ROLE, one entity per row, each a new-tab
    // profile link. A flat chip run is unreadable once a deal has a target, two bankers and two law
    // firms in one cell — which is exactly the cell these columns produce.
    if (CATCHALL_FIELD_KEYS.has(col.field_key)) {
      return <RoleGroupedRefs value={value} kind={kind} />;
    }
    // E2c: the same governed renderer the grid uses — one component, not a second copy that drifts.
    // Read-only here (no `onOpen`), so refs render as plain chips, but by governed NAME only: never
    // an email, never a bare id.
    return <EntityRefs value={value} kind={kind} />;
  }
  if (ft === 'multi_enum' || ft === 'enum' || ft === 'ranked_enum') {
    const colorMap = col.groups?.length ? colorMapFromGroups(col.groups) : {};
    const vals = cellValues(value).filter((s) => s !== '');
    if (vals.length === 0) return <span className={styles.gridEmpty}>—</span>;
    // The TONE outranks the option colour map here, deliberately. A proposal's own green and a struck
    // prior's grey are what tell the operator which tile is which; the option colour is decoration by
    // comparison, and keeping it would reproduce the two-identical-tiles problem in a different hue.
    const toneClass = tone === 'new' ? styles.pillProposed : tone === 'prior' ? styles.pillPrior : null;
    return (
      <span className={styles.gridChips}>
        {vals.map((v, i) => (
          <span
            key={i}
            className={`${styles.pill} ${toneClass ?? PILL_CLASS[colorMap[v.toLowerCase()]] ?? styles.pillPlain}`}
          >
            {/* W5 §10.3: the DISPLAY vocabulary from the column's server-owned `enum_labels`.
                W5 emits catalog columns on the panel surface too, so `__ai_proposed_row__` reaches
                here — and without this the panel showed an "AI proposed" pill for the row whose grid
                cell read "Proposed" (review findings 8/12). The colour map still keys on the RAW
                value, which is correct: option colours are configured against stored values. */}
            {enumDisplay(col, v)}
          </span>
        ))}
      </span>
    );
  }
  if (ft === 'currency') return <>${cellText(value)}</>;
  // COMPUTED date columns (last_email / last_meeting / … and every system-field date) show a
  // relative day + exact-date tooltip, matching the grid; a real editable `date` field stays a plain
  // ISO string. `isComputedColumn`, not `origin === 'derived'` — W5's §9.2 flip, see crmSystemFields.
  if (ft === 'date' && isComputedColumn(col)) {
    return <span title={fmtShortDate(value as string) || undefined}>{fmtRelativeDay(value as string)}</span>;
  }
  return <>{cellText(value)}</>;
}

// ── per-type inline editor (re-hosts the grid's editors vertically; spec §6.3) ───────────────────
function PopupEditor(
  { kind, col, initial, options, refPeople, onCommit, onCancel, singleRef, allowCreate }:
  { kind: 'checklistSingle' | 'checklistMulti' | 'ref' | 'rosterRef'; col: CrmGridColumn;
    initial: unknown;
    options: string[] | undefined;
    /** PR3 item 7: the roster picklist for a `ref_options_source:'roster'` column (`rosterRef`). */
    refPeople?: CrmRosterOption[];
    onCommit: (v: unknown) => void; onCancel: () => void;
    /** Force single-select for a ref editor regardless of the column's arity. E2e-3's Edit corrects
     * ONE fact, so even on a `*_multi` column the picker must return one entity — a multi picker
     * there would silently make "replace this company" read as "set the whole cell". */
    singleRef?: boolean;
    /** Offer the inline organisation create when the ref search finds nothing (fact-add only — see
     *  the render-prop call site below). Off everywhere else, so every existing host is unchanged. */
    allowCreate?: boolean },
) {
  // The AG editors stage their value through onValueChange and signal "commit" via stopEditing (their
  // Done button / a single-select pick). A ref holds the latest staged value so stopEditing commits it
  // synchronously (React state wouldn't have flushed yet within the same click handler).
  const stagedRef = useRef<unknown>(initial);
  const stub = {
    value: initial,
    onValueChange: (v: unknown) => { stagedRef.current = v; },
    stopEditing: () => onCommit(stagedRef.current),
    // PR3 item 8: the editors' ✕ needs the PANEL's discard — this container owns the commit, so
    // AG-Grid's `api.stopEditing(true)` does not exist here. Dropping the staged value and closing IS
    // the restore, because nothing is written per toggle (see `crmEditorCancel`).
    onCancelEdit: onCancel,
  } as unknown as CustomCellEditorProps;
  const refKind = col.field_type.startsWith('person') ? 'person' : 'organization';
  const single = singleRef ?? (col.field_type === 'person' || col.field_type === 'organization');
  return (
    <div className={styles.recordPopupEditor}>
      {kind === 'rosterRef'
        ? <CrmRosterPicker {...stub} people={refPeople ?? []} single={single} />
        : kind === 'ref'
          ? <RefCellEditor {...stub} kind={refKind} single={single} allowCreate={allowCreate} />
          : <CrmChecklistEditor {...stub} options={options ?? []} single={kind === 'checklistSingle'} />}
      <button className={styles.recordEditCancel} onClick={onCancel}>Cancel</button>
    </div>
  );
}

function ScalarEditor(
  { kind, initial, options, onCommit, onCancel }:
  { kind: PanelEditorKind; initial: unknown; options: string[] | undefined;
    onCommit: (v: unknown) => void; onCancel: () => void },
) {
  const asStr = initial == null ? '' : cellText(initial);
  const [val, setVal] = useState(kind === 'date' ? asStr.split('T')[0] : asStr);
  // One-shot guard: commit and cancel are mutually exclusive and fire at most once. Without it, an
  // Escape-to-cancel could still commit via the browser's blur-on-unmount (removing a focused input
  // dispatches blur → onBlur=commit would override the cancel).
  const doneRef = useRef(false);
  const commit = (v: unknown = val) => { if (doneRef.current) return; doneRef.current = true; onCommit(v); };
  const cancel = () => { if (doneRef.current) return; doneRef.current = true; onCancel(); };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); cancel(); }
    else if (e.key === 'Enter' && kind !== 'textarea') { e.preventDefault(); commit(); }
  };
  if (kind === 'textarea') {
    return (
      <div className={styles.recordScalarEditor}>
        <textarea className={styles.recordTextarea} autoFocus value={val} onKeyDown={onKey}
                  onChange={(e) => setVal(e.target.value)} />
        <div className={styles.recordEditActions}>
          <button className={styles.recordEditSave} onClick={() => commit()}>Save</button>
          <button className={styles.recordEditCancel} onClick={cancel}>Cancel</button>
        </div>
      </div>
    );
  }
  if (kind === 'select') {
    const opts = options ?? [];
    const cur = asStr;
    const shown = cur && !opts.includes(cur) ? [cur, ...opts] : opts;
    return (
      <select className={styles.recordSelect} autoFocus value={val} onKeyDown={onKey}
              onChange={(e) => commit(e.target.value)} onBlur={cancel}>
        <option value="">—</option>
        {shown.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  const type = kind === 'number' ? 'number' : kind === 'date' ? 'date' : 'text';
  return (
    <input className={styles.recordInput} type={type} autoFocus value={val} onKeyDown={onKey}
           onChange={(e) => setVal(e.target.value)} onBlur={() => commit()} />
  );
}

// ── one field row (label + value + status-driven affordances) ────────────────────────────────────
function RecordFieldRow(
  { col, value, state, editable, editorKind, options, refPeople, busy, historyIds, restTone,
    record, onEdit, onAccept, onDismiss }:
  { col: CrmGridColumn; value: unknown; state: CrmRecordFieldState | undefined;
    /** the resolved record row — carries `_system_*_state`, the §7.3 coverage meta a system
     *  cell's tooltip reads. Absent on surfaces that have no row; the tooltip then degrades to
     *  definition-only rather than guessing. */
    record?: Record<string, unknown> | null;
    editable: boolean; editorKind: PanelEditorKind | null; options: string[] | undefined;
    /** PR3 item 7: the roster picklist, when this column is roster-flagged. */
    refPeople?: CrmRosterOption[];
    busy: boolean;
    /** §15 item 9 — tone for the REST-STATE value. `'new'` on a whole-record AI proposal, where every
     *  value on the record is proposed and so should read as one. */
    restTone?: ValueTone;
    /** CUT-8: the cell's change history. Absent → no affordance (e.g. an unsaved row). */
    historyIds?: { listId: number | null; entryId: number | null };
    onEdit?: (value: unknown) => void;
    onAccept?: (proposalId: number | null) => void;
    onDismiss?: (proposalId: number | null) => void },
) {
  const [editing, setEditing] = useState(false);
  const status = state?.status ?? 'unchanged';
  const isProposed = status === 'proposed';
  const isEdited = status === 'edited';
  const isFailed = status === 'push_failed';
  const canEdit = editable && !!editorKind && !!onEdit && !isProposed;

  const commitEdit = (v: unknown) => { setEditing(false); onEdit?.(v); };

  return (
    <div className={`${styles.recordField} ${isFailed ? styles.recordFieldFailed : ''}`}>
      <div className={styles.recordFieldLabel}>
        <span>{col.label}</span>
        {isEdited && <span className={styles.recordDotBlue} role="img" title="Edited — pending sync to Affinity" aria-label="Edited — pending sync" />}
        {/* ⚠ **NOT on a computed column.** `lists.field_history` looks the key up in
            `crm_field_def` and raises "no field '<key>' on this list" for a catalog key — which by
            construction has none (the same structural fact the review-membership exclusion rests
            on). Default-on put 3 such rows on every opportunity record and 9 on every person record,
            each with a ⟲ that always answered "Couldn't load history." Gated by CAPABILITY
            (`isComputedColumn`), never a key list, so a catalog addition needs no release here. */}
        {historyIds && !isComputedColumn(col) && (
          <CrmFieldHistory listId={historyIds.listId} entryId={historyIds.entryId}
                           fieldKey={col.field_key} />
        )}
      </div>

      {editing && editorKind ? (
        editorKind === 'ref' || editorKind === 'rosterRef'
        || editorKind === 'checklistSingle' || editorKind === 'checklistMulti' ? (
          <PopupEditor kind={editorKind} col={col} initial={value} options={options} refPeople={refPeople}
                       onCommit={commitEdit} onCancel={() => setEditing(false)} />
        ) : (
          <ScalarEditor kind={editorKind} initial={value} options={options}
                        onCommit={commitEdit} onCancel={() => setEditing(false)} />
        )
      ) : isProposed ? (
        <div className={styles.recordRedline}>
          {/* proposed value FIRST, struck prior after (design-owner rule 2026-07-10) */}
          <span className={styles.recordRedlineNew}>{renderValue(col, state?.proposed_value, 'new')}</span>
          <span className={styles.recordRedlineOld}>{renderValue(col, value, 'prior')}</span>
          {/* aria-disabled (not `disabled`) so activating the button while a mutation is in flight
              does NOT blur it to <body> — that would collapse this opacity/focus-within-revealed action
              group and, on error, strand a keyboard user with the controls hidden (a11y review). The
              busy click is a no-op here AND guarded by the container's in-flight ref. */}
          <span className={styles.recordProposalActions}>
            <button className={styles.recordAccept} title="Accept this proposal" aria-label={`Accept proposal for ${col.label}`}
                    aria-disabled={busy || undefined} onClick={() => { if (!busy) onAccept?.(state?.proposal_id ?? null); }}>✓</button>
            <button className={styles.recordDismiss} title="Dismiss this proposal" aria-label={`Dismiss proposal for ${col.label}`}
                    aria-disabled={busy || undefined} onClick={() => { if (!busy) onDismiss?.(state?.proposal_id ?? null); }}>✗</button>
          </span>
        </div>
      ) : (
        <div className={styles.recordFieldValue}>
          <span className={restTone === 'new' ? styles.recordRedlineNew : styles.recordFieldValueText}
                title={systemTitle(col, record, value)}>
            {renderValue(col, value, restTone ?? 'plain')}
          </span>
          {canEdit && (
            <button className={styles.recordEditBtn} aria-label={`Edit ${col.label}`} title="Edit"
                    aria-disabled={busy || undefined} onClick={() => { if (!busy) setEditing(true); }}>✎</button>
          )}
          {/* A field whose last Affinity push FAILED renders its error here. UNREACHABLE since the
              Pillar-D write funeral (crm-of-record-plan §5, 2026-08-27) deleted the drain that was the
              only writer of `crm_field_user_state.push_error` — the column is kept, so a HISTORICAL
              row would still render, which is why the display stays and only the action went. The
              "Retry" button next to it fired `useSyncList`; there is nothing left to retry. */}
          {isFailed && (
            <div className={styles.recordPushError} role="alert">
              <span>{state?.push_error || 'Affinity sync failed.'}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── R5/R4-1/S4: one field row driven by record_review.v2 allowed_actions (PROPOSED or COMMITTED) ───
// Distinct from the v1 RecordFieldRow (untouched). The server-computed `allowed_actions` drive which
// affordances render. S4 (owner-confirmed 2026-07-24): NO field-level Accept on either lifecycle — a
// proposed draft is resolved only by the record-level Accept (spec §2.2, unchanged); a committed field
// is resolved by editing it (which publishes immediately, CRM-local) or by the record-level "Accept
// all" sweeping whatever is left untouched (S3). A COMMITTED field with a pending proposal instead gets
// TWO edit pencils — black "edit current" (settle on today's value, e.g. to dismiss a proposal without
// a reason) and green "edit proposal" (settle on the AI's incoming value, possibly tweaked) — since a
// committed row has a genuine current-vs-proposed split a draft row doesn't; a PROPOSED row keeps its
// existing single edit-the-draft pencil (`isProposedRow`). Both edit affordances commit via the SAME
// `onDraftEdit` — the container routes it by lifecycle + proposal id (draft-override / edit-and-accept
// / plain edit). Colors reuse the redline vocabulary: green = proposed/override value, struck = prior
// value, blue dot = operator override, struck/grey "(rejected)" = a rejected proposal.
function ReviewFieldRow(
  { col, field, editable, editorKind, options, refPeople, busy, isProposedRow, restTone, historyIds,
    record, onDraftEdit, onReject, onRestore }:
  { col: CrmGridColumn; field: RecordReviewV2Field;
    editable: boolean; editorKind: PanelEditorKind | null; options: string[] | undefined;
    /** PR3 item 7: the roster picklist, when this column is roster-flagged. */
    refPeople?: CrmRosterOption[];
    busy: boolean;
    isProposedRow: boolean;
    /** §15 item 9 — tone for the rest-state value on a whole-record AI proposal. */
    restTone?: ValueTone;
    /** CUT-8 cell history — rendered ONLY on a resolved committed member (see below). */
    historyIds?: { listId: number | null; entryId: number | null };
    onDraftEdit?: (value: unknown) => void;
    /** the resolved record row — the §7.3 coverage meta a system cell's tooltip reads. */
    record?: Record<string, unknown> | null;
    onReject?: (proposalId: number | null) => void;
    onRestore?: (proposalId: number | null) => void },
) {
  const [editing, setEditing] = useState<'current' | 'proposal' | null>(null);
  const actions = field.allowed_actions ?? [];
  const canEdit = editable && actions.includes('edit') && !!editorKind && !!onDraftEdit;
  // Reject / Restore gate on the SERVER's allowed_actions alone — not on `editable`, which here means
  // "this type has an inline editor" (panelFieldEditor). Conflating the two hid the ✗ Reject on a
  // qualitative BLOCK, which has no inline editor by design yet is perfectly rejectable; the v1 path
  // offers it, so gating it out would have been a regression. The server already encodes editability:
  // `_field_actions` returns [] when the list isn't editable, so allowed_actions is the single source.
  const canReject = actions.includes('reject') && !!onReject;
  const canRestore = actions.includes('restore') && !!onRestore;
  const disp = field.disposition;
  const isRejected = disp === 'rejected' || disp === 'retracted';
  const isDraftEdited = disp === 'edited';
  // D4 (review §7, findings U1/U2): a COMMITTED member field whose latest proposal is RESOLVED —
  // accepted / superseded / edited / rejected / retracted. There is no live proposal to choose between,
  // so the ONLY edit is a plain edit of the value in force (the container routes it to the cell PATCH,
  // never edit-and-accept). That makes the dual-pencil vocabulary wrong here: without this, such a
  // field rendered a LONE pencil styled GREEN and labelled "Edit proposed value" / "Edit proposal for
  // X", seeding `proposed_value` — i.e. an "edit the proposal" affordance for a proposal that is
  // already closed, on the value the operator was not looking at (finding F-7).
  // ⚠ Shared with the container's row routing (`isResolvedCommittedMember`) ON PURPOSE — the row
  // that renders the pencil and the handler that picks its write route must answer this from ONE
  // predicate. It is NOT `disposition !== 'pending'`: an overlaid pending proposal also reports
  // 'edited'; see the helper for why the server's plain-'edit' sanction is the discriminator.
  const isResolvedMember = isResolvedCommittedMember(field, isProposedRow);
  // A field with active proposal content to review: a pending AI proposal, or an operator draft override.
  // NOT a RESOLVED 'edited' committed member (a settled edit-and-accept, or an overlay over an accepted
  // value): nothing is awaiting review there, so it renders as the plain value in force (blue "Edited"
  // dot, one black pencil, no ✗) — painting it in the green redline read as a pending proposal the
  // operator could not accept, and its ✗ hit a reject the server refuses (409, `resolution IS NULL`).
  const hasContent = disp === 'pending' || (isDraftEdited && !isResolvedMember);
  const pid = field.proposal_id ?? null;
  // Edit seeds by WHICH pencil was clicked. 'current' → the settled value (committed rows only; a
  // proposed draft's current_value is always empty, so no pencil offers it there). 'proposal' → the
  // SAME value the green redline displays: effective_value when a draft override is already in flight
  // (isDraftEdited), else the AI's proposed_value. NOTE: for a committed PENDING (non-overlaid) field,
  // effective_value equals current_value (not null, unlike on a proposed row) — so this must branch on
  // isDraftEdited explicitly rather than `effective_value ?? proposed_value`, which would silently
  // resolve to the CURRENT value on a committed row and make the two pencils seed identically.
  // (memo §9 editSeed lesson: branch EXPLICITLY per case — never `effective_value ?? proposed_value`.)
  // A resolved member's single pencil edits the value IN FORCE, which is exactly what the row renders
  // (`effective_value`): the settled value for accepted/superseded/rejected, the operator's edited value
  // for 'edited'. Its `proposed_value` is closed history and must never seed an edit.
  const editSeed = editing === 'current'
    ? field.current_value
    : isResolvedMember
      ? field.effective_value
      : (isDraftEdited ? field.effective_value : field.proposed_value);
  // The prior/current value, struck in the redline (committed field-updates) — empty for a fresh draft.
  const priorValue = field.current_value;
  // S5b: a qualitative BLOCK is ADDITIVE, not a replacement — its pending bullets are new narrative
  // accumulated since the last accept, and the "prior" is the previously-accepted narrative, which is
  // not being superseded. Striking it through would assert a replacement that isn't happening (and on a
  // long block would double the row's height for no information). Scalars keep the redline.
  const isBlock = col.field_type === BLOCK_FIELD_TYPE;
  // Only show the struck prior on a REAL change — hide it when the proposal equals current (a no-op /
  // re-proposed value) so the value doesn't render twice (the committed-row duplication bug).
  const showPrior = !isBlock && !isDraftEdited && priorValue != null && priorValue !== ''
    && !sameCellValue(field.proposed_value, priorValue);
  // The value in force beside a rejected/retracted proposal (committed rows; empty on a draft).
  const standing = field.effective_value;
  const hasStandingValue = standing != null && standing !== ''
    && !(Array.isArray(standing) && standing.length === 0);

  // ── A qualitative BLOCK's own editor: drop individual bullets, accept the rest ─────────────────
  //
  // `canEdit` above cannot serve this and must not be made to. It requires `editorKind` — the shared
  // `panelFieldEditor` vocabulary, which the LISTS GRID reads from the same helper — and a block has
  // no scalar editor by design. Teaching that helper that `qualitative_block` is "editable" would
  // hand the grid an editor for a value no cell editor can represent. So the special case lives here,
  // where the special case is, and the shared type vocabulary is left alone.
  //
  // Nothing new is sanctioned: the server already returns ('edit','reject') on a pending block, where
  // 'edit' means `review-edit-accept`. The pencil was simply never rendered, so an operator faced with
  // a four-bullet block containing one wrong bullet could only publish the error or reject all four —
  // and rejecting loses the rest for good (the producer's standing set reads PENDING rows only). The
  // case that found it: a New Info bullet on Marine Equipment Solutions asserting a trading update
  // that belonged to Project Pineapple. See docs/plans/strategic-architecture/
  // recent-updates-evidence-provenance-and-retraction-spec.md.
  //
  // A RESOLVED member is excluded: its 'edit' is the plain cell PATCH (D4), not edit-and-accept, and
  // dropping bullets from a settled value is a different act with a different route.
  const canEditBlock = isBlock && disp === 'pending' && !isResolvedMember
    && actions.includes('edit') && !!onDraftEdit;
  // Keyed by the PROPOSAL, not just "open": this row is not remounted when its content changes, so a
  // refetch mid-edit would otherwise leave ids composed against the old proposal applied to the new
  // one — dropping bullets the operator never saw. Same guard as the Recent-Updates cluster's.
  const [blockEdit, setBlockEdit] = useState<{ proposalId: number | null; dropped: Set<string> } | null>(null);
  const blockSession = blockEdit != null && blockEdit.proposalId === pid && canEditBlock ? blockEdit : null;
  const blockItems = Array.isArray(field.proposed_value) ? field.proposed_value : [];
  const blockKept = blockSession ? editedBlockValue(field.proposed_value, blockSession.dropped) : blockItems;
  // Nothing dropped IS Accept-all's job and everything dropped IS Reject; sending either through
  // edit-and-accept would stamp a misleading `edited_accepted` on the ledger.
  const canSaveBlockEdit = blockSession != null
    && blockKept.length > 0 && blockKept.length < blockItems.length;
  const toggleBlockItem = (itemId: string) => setBlockEdit((prev) => {
    if (prev == null || prev.proposalId !== pid) return prev;
    const dropped = new Set(prev.dropped);
    if (!dropped.delete(itemId)) dropped.add(itemId);
    return { ...prev, dropped };
  });
  const blockEditBtn = canEditBlock && !blockSession ? (
    <button className={styles.recordEditProposalBtn} title="Remove individual bullets, then accept the rest"
            aria-label={`Edit proposal for ${col.label}`}
            aria-disabled={busy || undefined}
            onClick={() => { if (!busy) setBlockEdit({ proposalId: pid, dropped: new Set() }); }}>✎</button>
  ) : null;

  const commitEdit = (v: unknown) => { setEditing(null); onDraftEdit?.(v); };
  // Dual pencils only make sense on a COMMITTED row with a LIVE proposal to choose between; a proposed
  // draft keeps its single "edit the draft" pencil (green, since it's editing the incoming AI value),
  // and a resolved member (D4) keeps ONE BLACK "Edit X" pencil on the value in force.
  const dualEdit = canEdit && !isProposedRow && !isResolvedMember;
  const editCurrentBtn = dualEdit ? (
    <button className={styles.recordEditBtn} title="Edit current value" aria-label={`Edit current ${col.label}`}
            aria-disabled={busy || undefined} onClick={() => { if (!busy) setEditing('current'); }}>✎</button>
  ) : null;
  const editProposalBtn = canEdit ? (
    <button className={dualEdit ? styles.recordEditProposalBtn : styles.recordEditBtn}
            title={dualEdit ? 'Edit proposed value' : 'Edit'}
            aria-label={dualEdit ? `Edit proposal for ${col.label}` : `Edit ${col.label}`}
            aria-disabled={busy || undefined} onClick={() => { if (!busy) setEditing('proposal'); }}>✎</button>
  ) : null;

  return (
    <div className={styles.recordField}>
      <div className={styles.recordFieldLabel}>
        <span>{col.label}</span>
        {isDraftEdited && <span className={styles.recordDotBlue} role="img"
          title={isProposedRow ? 'Draft edit — applied when the record is accepted' : 'Edited'}
          aria-label="Draft edit" />}
        {/* CUT-8 history, and ONLY on a resolved committed member: those are exactly the fields this
            slice moved off the v1 row, which rendered this affordance — so keeping it is no-regression
            rather than a new surface. Pending fields never had it here and finding L1 (field_history is
            entry-gated only, no per-field gate) is open, so this slice does not widen it. */}
        {isResolvedMember && historyIds && !isComputedColumn(col) && (
          <CrmFieldHistory listId={historyIds.listId} entryId={historyIds.entryId}
                           fieldKey={col.field_key} />
        )}
      </div>

      {blockSession ? (
        /* The block's editor: the same bullets, each with its own drop toggle. Painted in the
           redline's green tone because what is being composed IS the proposal — the operator is
           choosing which of its bullets to publish, not authoring a different value. */
        <div className={styles.recordRedline}>
          <CrmBlockRuns groups={groupBlockItems(field.proposed_value)} tone="new"
                        listClassName={styles.blockBulletsNew}
                        droppedItemIds={blockSession.dropped} onToggleItem={toggleBlockItem} />
          <span className={styles.recordProposalActions}>
            <button className={styles.recordEditProposalBtn}
                    aria-disabled={busy || !canSaveBlockEdit || undefined}
                    title={canSaveBlockEdit
                      ? 'Accept the bullets you kept'
                      : blockKept.length === 0
                        ? 'Every bullet is removed — use ✗ to reject the whole proposal instead'
                        : 'Remove at least one bullet, or use Accept all to take the proposal as-is'}
                    onClick={() => {
                      if (busy || !canSaveBlockEdit) return;
                      setBlockEdit(null);
                      onDraftEdit?.(blockKept);
                    }}>✓ Accept {blockKept.length} of {blockItems.length}</button>
            <button className={styles.recordEditBtn} title="Discard these changes"
                    aria-label={`Cancel editing ${col.label}`}
                    onClick={() => setBlockEdit(null)}>Cancel</button>
          </span>
        </div>
      ) : editing && editorKind ? (
        editorKind === 'ref' || editorKind === 'rosterRef'
        || editorKind === 'checklistSingle' || editorKind === 'checklistMulti' ? (
          <PopupEditor kind={editorKind} col={col} initial={editSeed} options={options} refPeople={refPeople}
                       onCommit={commitEdit} onCancel={() => setEditing(null)} />
        ) : (
          <ScalarEditor kind={editorKind} initial={editSeed} options={options}
                        onCommit={commitEdit} onCancel={() => setEditing(null)} />
        )
      ) : isRejected ? (
        <div className={styles.recordFieldValue}>
          {/* The value IN FORCE comes first: on a committed row a rejected proposal leaves the standing
              value standing, and rendering only the struck AI value hid it entirely. A proposed (draft)
              row has nothing standing yet (effective_value empty by construction), so it shows only the
              struck proposal, as before. The struck copy is skipped when it equals the standing value
              so the value never renders twice; "(rejected)" + ↺ still mark the closed proposal. */}
          {hasStandingValue && (
            <span className={styles.recordFieldValueText} title={systemTitle(col, record, field.effective_value)}>
              {renderValue(col, field.effective_value, 'plain')}
            </span>
          )}
          {!(hasStandingValue && sameCellValue(field.proposed_value, field.effective_value)) && (
            <span className={styles.recordRedlineOld}>
              {renderValue(col, field.proposed_value, 'prior')}
            </span>
          )}
          <span className={styles.gridEmpty}>(rejected)</span>
          {canRestore && (
            <button className={styles.recordEditBtn} title="Restore this proposal for review"
                    aria-label={`Restore proposal for ${col.label}`}
                    aria-disabled={busy || undefined} onClick={() => { if (!busy) onRestore?.(pid); }}>↺</button>
          )}
          {/* D4: a rejected proposal is resolved, so the field is plainly editable again — the server
              now offers ('restore','edit') here. Rendering only ↺ would leave 'edit' a dead action. */}
          {editProposalBtn}
        </div>
      ) : hasContent ? (
        <div className={styles.recordRedline}>
          {/* proposed/override value first (green); struck prior after (committed field-updates). */}
          <span className={styles.recordRedlineNew}>
            {renderValue(col, isDraftEdited ? field.effective_value : field.proposed_value, 'new')}
          </span>
          {showPrior && (
            <span className={styles.recordRedlineOld}>{renderValue(col, priorValue, 'prior')}</span>
          )}
          <span className={styles.recordProposalActions}>
            {/* S4: no field-level Accept on either lifecycle — editing a committed field publishes
                immediately (CRM-local); the record-level "Accept all" (S3) sweeps whatever is left. */}
            {editCurrentBtn}
            {editProposalBtn}
            {/* A block's pencil is its own (see canEditBlock): the shared editorKind vocabulary has no
                editor for this type, and `editProposalBtn` is therefore always null here. */}
            {blockEditBtn}
            {canReject && (
              <button className={styles.recordDismiss} title="Reject this proposal"
                      aria-label={`Reject proposal for ${col.label}`}
                      aria-disabled={busy || undefined} onClick={() => { if (!busy) onReject?.(pid); }}>✗</button>
            )}
          </span>
        </div>
      ) : (
        // Non-member field on the draft (no proposal): the plain effective value + an author-a-draft Edit.
        // §15 item 9: on a whole-record proposal this value IS part of the proposal, so it tones with it.
        <div className={styles.recordFieldValue}>
          <span className={restTone === 'new' ? styles.recordRedlineNew : styles.recordFieldValueText}
                title={systemTitle(col, record, field.effective_value)}>
            {renderValue(col, field.effective_value, restTone ?? 'plain')}
          </span>
          {editProposalBtn}
        </div>
      )}
    </div>
  );
}

// ── E2e-3 §4.3.6: a PROJECTED column — a published relationship fact worked through the standard
// AI-field rubric. Reject / Edit / Accept, PER CHIP, because each chip is a distinct fact: a deal can
// carry two seller orgs and rejecting one must not touch the other. The affordances come from the
// server's `projected_facts` (which already dropped any chip whose target this viewer may not see), so
// the panel never offers an action the endpoint would refuse. Each chip carries EVERY fact behind it
// with the `revision` it is rendered at, and the whole list goes back on an action: deciding one of
// two retracts half the value (Reject) or leaves two entities live at once (Edit).
//
// There is no "no proposal → nothing to do" state here: a projected value publishes on the fact
// (§2.2d, no review queue) and is corrected afterwards. So the chips render as the ordinary value
// and the actions reveal on hover/focus, exactly like the proposal redline's.
function ProjectedFieldRow(
  { col, chips, busy, addOptions, onOpenEditor, onOpenAdd }:
  { col: CrmGridColumn; chips: CrmProjectedChip[]; busy: boolean;
    /** Present ⇒ this column can express an add, so the field-level `+ Add` renders. */
    addOptions?: CrmProjectedAddOptions;
    /** §1.1: the pencil. Opens the modal on ONE chip's facts. */
    onOpenEditor?: (chip: CrmProjectedChip) => void;
    /** §1.3: the field-level `+ Add` — the SAME modal, with no entity preselected. */
    onOpenAdd?: () => void },
) {
  const contradictions = useContext(PartyContradictionsContext);
  // §15 item 2: role groups, on the catch-all columns only. `null` when this column is not one of
  // them, which selects the original flat chip run — so every other projected column is unchanged.
  const grouped = useMemo<{ role: string | null; party: string | null;
                            chips: CrmProjectedChip[] }[] | null>(() => {
    if (!CATCHALL_FIELD_KEYS.has(col.field_key)) return null;
    // ⚠ The SAME grouping as `RoleGroupedRefs`, from the SAME key, and that is load-bearing rather than
    // tidy: the panel renders a fact-backed field through both components depending on hydration, and a
    // difference between them is visible as the field re-laying itself out a moment after opening — the
    // "jumpy display twice thing". §1.5(a)'s party level had to land in both or reintroduce it.
    const groups = new Map<string, { role: string | null; party: string | null;
                                     chips: CrmProjectedChip[] }>();
    for (const f of chips) {
      for (const [role, parties] of rolePartyPairs(f.ref)) {
        for (const party of parties) {
          const key = `${role ?? ''}|${party ?? ''}`;
          const bucket = groups.get(key);
          if (bucket) bucket.chips.push(f);
          else groups.set(key, { role, party, chips: [f] });
        }
      }
    }
    // The SAME comparator the pre-hydrate renderer uses — headless last (see `compareRoleGroups`).
    return [...groups.values()].sort(compareRoleGroups);
  }, [col.field_key, chips]);

  // Party-facts lever D. A `party_tree` column produces no `projected_facts` today — the projection
  // keeps trees out of `ProjectionResult.facts`, so this component is never selected for `parties` —
  // and the branch is here anyway, deliberately: the field must render as the TREE on whichever
  // renderer the panel picks, or a payload change upstream would silently fall through to the flat
  // chip run and lose the grouping, the badge and the sort in one go.
  //
  // ⚠ AFTER the `useMemo` above, not before it: an early return ahead of a hook makes that hook
  // conditional, which is a rules-of-hooks error and, at runtime, a component whose hook order
  // changes with its props.
  if (col.field_type === PARTY_TREE_FIELD_TYPE) {
    return <CrmPartiesField value={chips.length > 0 ? chips[0].ref : null} />;
  }

  /**
   * Every relationship behind one chip, in full, on hover (owner ask, 2026-08-07).
   *
   * The row is deliberately terse — a name under a role heading — so the detail that made the fact what
   * it is (the other qualifiers, the side, the free-text descriptor, and whether a person or the AI put
   * it there) has nowhere to show. The pencil opens the editor for CHANGING it; this is for reading it,
   * which is the far more common act and should not cost a modal.
   *
   * CSS-only, on hover AND focus-within (the house `.syncDotTooltip` pattern) so it is reachable from
   * the keyboard, and `pointer-events: none` on the card so it can never intercept the pencil.
   *
   * **Owner item 2 (2026-08-09) — four readability fixes, all display-only.** As first shipped the card
   * crammed one fact onto one flowing line (`.factHoverRow` was an inline span with no `display`, so the
   * "rows" did not break at all) and printed everything the payload held:
   *
   *   (a) a VACUOUS `deal_participation` line is dropped — E5a's legacy conversion, which asserts
   *       participation and then says nothing about it. See `isVacuousParticipation` for why the test
   *       is emptiness rather than "a specific type also exists", which deleted real answers.
   *   (b) `unclassified` qualifier values are skipped. Every value used to go through `formatRole`, so a
   *       legacy `deal_party_role='unclassified'` printed "· UNCLASSIFIED ·" — the absence of a role,
   *       shouted. Same rule as the group headings (see `UNCLASSIFIED_ROLE`).
   *   (c) the side goes through `partyHeading`: "Buy side", not the raw slug word-cased to "Buy".
   *   (d) ONE LINE PER THING, in the order the reader needs them: relationship type → its qualifiers →
   *       who said it → the person's title and employer.
   */
  const factHoverCard = (f: CrmProjectedChip) => {
    // A fact with no TYPE has nothing to describe — an older payload, or a caller that only ever needed
    // the assertion id. Nothing here is worth throwing a panel for.
    const detailed = (f.facts ?? []).filter((fct) => !!fct?.relationship_type);
    // (a) — drop the lines that state nothing. The fallback is not defensive tidiness: an entity whose
    // ONLY fact is a legacy conversion still has to render something, and "Deal Participation, from the
    // AI" is at least true. Suppression must never be the reason a card goes blank.
    const kept = detailed.filter((fct) => !isVacuousParticipation(fct));
    const shown = kept.length > 0 ? kept : detailed;
    if (shown.length === 0) return null;
    const subtitle = personSubtitle(f.ref);
    return (
      <span className={styles.factHoverCard} role="tooltip">
        {shown.map((fct) => {
          // The role keys first, in the order the register lists them, then the descriptive one —
          // "Legal · Buy side" reads as the fact; "ran the stapled financing" is the colour on top.
          // Defensive: a fact from an older payload (or a hand-built fixture) may carry no qualifiers
          // at all, and a read-only hover must never be the thing that throws on a panel.
          const quals = fct.qualifiers ?? {};
          const descriptor = quals[DESCRIPTOR_QUALIFIER_KEY];
          const meta = Object.entries(quals)
            // (b) — `unclassified` (and empty) say nothing, so they print nothing.
            .filter(([k, v]) => k !== DESCRIPTOR_QUALIFIER_KEY && isMeaningfulRole(v))
            // (c) — the side is the one key with a phrasing of its own; `partyHeading` returns null for
            // `mutual` / `unknown`, which are not positions, so those fall back to the plain label.
            .map(([k, v]) => (k === SIDE_QUALIFIER_KEY ? partyHeading(v) ?? formatRole(v)
              : formatRole(v)));
          return (
            <span key={fct.assertion_id} className={styles.factHoverFact}>
              {/* (d) line 1 — WHAT the relationship is. */}
              <span className={styles.factHoverLine}>
                <span className={styles.factHoverType}>{formatRole(fct.relationship_type)}</span>
              </span>
              {/* (d) line 2 — its qualifiers, the free-text descriptor last and quoted. Absent
                  entirely when the fact carries neither, rather than an empty rule-separated line. */}
              {(meta.length > 0 || descriptor) && (
                <span className={styles.factHoverLine}>
                  {meta.length > 0 && <span className={styles.factHoverMeta}>{meta.join(' · ')}</span>}
                  {descriptor && (
                    <span className={styles.factHoverDescriptor}>
                      {meta.length > 0 ? ' — ' : ''}{`“${descriptor}”`}
                    </span>
                  )}
                </span>
              )}
              {/* (d) line 3 — WHO said it. */}
              <span className={styles.factHoverLine}>
                <span className={styles.factHoverMeta}>{factProvenance(fct)}</span>
              </span>
            </span>
          );
        })}
        {/* (d) last — the person's title and employer, which describe the ENTITY rather than any one
            fact, so they sit below all of them instead of inside the first. */}
        {subtitle && (
          <span className={styles.factHoverRow}>
            <span className={styles.factHoverMeta}>{subtitle}</span>
          </span>
        )}
      </span>
    );
  };

  /** One chip's name + its pencil. Shared by the grouped and flat layouts so the two cannot drift. */
  const chipBody = (f: CrmProjectedChip) => {
    const name = (f.ref.name ?? '').trim() || '(unnamed)';
    // §3.3 — same rule as the pre-hydrate `RoleGroupedRefs` renderer above, and it has to be: this
    // component takes over the SAME field once `projectedFacts` arrive, so a link condition applied to
    // only one of them makes a colleague's name flip from text to a dead link a moment after opening.
    const id = canOpenProfile(f.ref) && typeof f.ref.local_id === 'number' ? f.ref.local_id : null;
    return (
      <>
        {/* Name only — never the id, never an email (E2c). `(unnamed)` is an AUTHORIZED target with no
            name on record; the server already omitted withheld ones. On a catch-all column the name is
            a new-tab profile link (§15 item 2); elsewhere it stays plain text, unchanged. */}
        <span className={styles.factHoverAnchor}>
          {grouped && id != null ? (
            <a className={styles.gridRefLink} href={profileHref(f.kind === 'person' ? 'person' : 'organization', id)}
               target="_blank" rel="noopener noreferrer">{name}</a>
          ) : (
            <span className={styles.recordProjectedName}>{name}</span>
          )}
          {factHoverCard(f)}
        </span>
        {/* Same subtitle as the pre-hydrate renderer — the render-flip bug was two renderers
            disagreeing, so anything added to one is added to both. P1 puts it on the SAME line, with
            the separator as real text, exactly as `RoleGroupedRefs` does. */}
        {grouped && personSubtitle(f.ref) && (
          <>{', '}<span className={styles.roleGroupSubtitle}>{personSubtitle(f.ref)}</span></>
        )}
        {/* P3 — the ⚠, from the same index the pre-hydrate renderer reads, so it does not flicker
            across the hydrate. */}
        {grouped && (
          <ContradictionBadge
            entries={(typeof f.ref.local_id === 'number'
              ? contradictions.get(contradictionKey(f.kind, f.ref.local_id)) : undefined) ?? []} />
        )}
        {/* §1.1 — ONE affordance per row: the pencil, and nothing else.

            What was here: an inline role `<select>` plus ✓ / ✎ / ✗. All four are gone, and that is the
            owner's directive rather than a simplification. §0 case 3 measured why the row cannot carry
            what the data needs: a two-qualifier fact has no room, a chip whose facts span two types got
            NO dropdown at all (and the catch-alls are exactly those columns, which is why advisers
            routinely had none), the TYPE could not be changed, and a removal had nowhere to ask for its
            mandatory reason. Keeping both paths would leave two editors with different capabilities,
            which is how the confusion arose in the first place.

            Every operation those buttons performed still exists — Accept, remove-with-reason and the
            entity swap all live in the modal now, per FACT rather than per chip. The pencil appears
            wherever a chip does, INCLUDING the multi-type chips that previously offered nothing. */}
        <span className={styles.recordChipActions}>
          {/* WP-6b — "why?" beside the pencil, from the SAME `facts` list the modal edits, so the
              question and the correction address one set of claims. Ids only: every one of them
              already cleared the entity gate and WP-5b's class gate in `projected_fact_actions`, and
              a chip whose every claim was withheld never reached this render at all. */}
          <FactWhyButton assertionIds={(f.facts ?? []).map((fct) => fct.assertion_id)}
                         label={f.ref.name ?? 'this value'} />
          <button className={styles.recordEditBtn} title="Edit this row's relationships"
                  aria-label={`Edit ${f.ref.name ?? 'value'} on ${col.label}`}
                  aria-disabled={busy || undefined}
                  onClick={() => { if (!busy) onOpenEditor?.(f); }}>✏</button>
        </span>
      </>
    );
  };

  return (
    <div className={styles.recordField}>
      <div className={styles.recordFieldLabel}>
        <span>{col.label}</span>
        {/* §1.3 — the field-level `+ Add` opens the SAME modal with no entity preselected, so the add
            path and the edit path are one surface rather than two with different capabilities.
            Rendered wherever the server says the column can express an add, which since the slot
            contract includes the catch-alls and the multi-qualifier types the old inline row could
            not: the type and its qualifiers are chosen IN the modal, so nothing has to fit one row.

            It sits ON THE LABEL LINE (owner, 2026-08-09). Below the chips it was one scroll past the
            bottom of a long catch-all field, so "how do I add a company here" was answered off-screen;
            beside the label it is where the eye already is when it decides the field is short a name.
            The amber "AI" dot that used to occupy this slot is gone with it — every chip already says
            its provenance in the hover card, and the dot said it a second time for the whole field. */}
        {onOpenAdd && addOptions && addOptions.types.length > 0 && (
          <button type="button" className={`${styles.recordAddBtn} ${styles.recordAddInline}`}
                  disabled={busy}
                  title={`Add to ${col.label}`} aria-label={`Add to ${col.label}`}
                  onClick={onOpenAdd}>+ Add</button>
        )}
      </div>
      {(
        <div className={styles.recordFieldValue}>
          {/* ⚠ **The catch-all columns group here TOO, and this is why.** The panel renders a
              fact-backed field through TWO different components depending on hydration: before the
              record endpoint answers, `renderValue` → `RoleGroupedRefs` (grouped by role); once
              `projectedFacts` arrive, THIS component takes over. With a flat chip run here, the field
              visibly flipped from the grouped layout to an undifferentiated list a moment after
              opening — reported by the owner as "a jumpy display twice thing". Two renderers for one
              field must agree, so the grouping lives in both. */}
          {grouped ? (
            <div className={styles.roleGroups}>
              {grouped.map(({ role, party, chips: members }) => (
                <div key={`${role ?? '—unlabelled'}|${party ?? ''}`} className={styles.roleGroup}>
                  {/* P1 — ONE heading line, identical to the pre-hydrate renderer's. */}
                  {groupHeading(role, party) && (
                    <div className={styles.roleGroupLabel}>{groupHeading(role, party)}</div>
                  )}
                  <div className={memberColumnsClass(
                    members.map((f) => partyLineText(f.ref.name ?? null, f.ref)))}>
                    {members.map((f) => (
                      <div key={`${role ?? ''}-${party ?? ''}-${f.ref.local_id}`}
                           className={styles.roleGroupRow}>
                        {chipBody(f)}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <span className={styles.gridChips}>
              {chips.map((f) => (
                <span key={f.ref.local_id} className={styles.recordProjectedChip}>{chipBody(f)}</span>
              ))}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export interface RecordPanelProps {
  /** The loaded record row (field values keyed by field_key + id / _subject_kind / _subject_id). The
   * container passes the authoritative endpoint row once hydrated, else the fast grid-row snapshot
   * (R3). Undefined while a not-in-loaded-set record is still fetching. */
  record: Record<string, unknown> | undefined;
  /** The column schema (labels, types, colored groups). */
  columns: CrmGridColumn[];
  /** 1-based position in the current filtered/sorted set, for the "n of N" indicator. Null while unknown. */
  position: { index: number; total: number } | null;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  /** True when the open record was filtered OUT of the current displayed set (§6.2). */
  staleInView?: boolean;
  /** Membership rows only (person/org): jump to the full CrmProfile. */
  onOpenProfile?: (kind: 'person' | 'organization', id: number) => void;

  // ── D2b actionable props (present ⇔ the authoritative payload has hydrated; §6.5) ──
  /** Authoritative resolved field state keyed by field_name. ABSENT → read-only (hydrate-before-actionable). */
  fieldStates?: Record<string, CrmRecordFieldState>;
  editMode?: 'direct' | 'overlay';
  /** Enum picklists (field_key → option labels) for select / checklist editors. */
  options?: Record<string, string[]>;
  /** PR3 item 7: eligible-people picklists (field_key → roster options) for the ref columns flagged
   *  `ref_options_source:'roster'` — `deal_team` / `deal_lead` get a checklist, not a typeahead. */
  refOptions?: Record<string, CrmRosterOption[]>;
  /** plan 1b: `stage_mapping` ladder membership, so the Stage editor offers the ladder this record's
   *  `deal_type` calls for. Absent/null ⇒ every enum editor behaves exactly as before. */
  stageLadders?: CrmStageLadders | null;
  /** Record endpoint in flight (paints read-only from the snapshot meanwhile). */
  loading?: boolean;
  /** Endpoint 404 — existence-safe "not available" state (never distinguishes missing vs class-denied). */
  notFound?: boolean;
  /** Last mutation / stale-409 error to surface as a dismissible banner. */
  actionError?: string | null;
  /** field_key currently mutating → disable its controls (prevents double-submit). */
  busyField?: string | null;
  onEditField?: (fieldKey: string, value: unknown) => void;
  /** Rename a PROPOSED (draft) record's title — candidate data, not a cell (the D8 rule refuses
   * cell edits on drafts), so it routes to the dedicated rename-draft endpoint. */
  onRenameDraft?: (name: string) => void;
  onAcceptProposal?: (fieldKey: string, proposalId: number | null) => void;
  onDismissProposal?: (fieldKey: string, proposalId: number | null) => void;
  /* `onRetryPush` — retry a failed Affinity push by draining the list's pending overlay — was removed
   * with the drain itself by the Pillar-D write funeral (crm-of-record-plan §5, 2026-08-27). */

  // ── R5/R4-1/S4 (per-field review; record_review.v2). Present ⇔ the container hydrated v2 for this
  // row (PROPOSED or COMMITTED); each keyed field renders via ReviewFieldRow (allowed_actions-driven:
  // edit / reject / restore — NEVER a field-level Accept on either lifecycle, S4). Absent → v1
  // rendering (flag dark, or a committed field the v2 gate excludes). ──
  reviewFields?: Record<string, RecordReviewV2Field>;
  /** S4: true for a PROPOSED (draft) row — ReviewFieldRow keeps its single edit-the-draft pencil there;
   * a COMMITTED row (false) gets the dual edit-current/edit-proposal pencils instead. */
  reviewIsProposed?: boolean;
  onDraftEdit?: (fieldKey: string, value: unknown) => void;
  onRejectProposal?: (fieldKey: string, proposalId: number | null) => void;
  onRestoreProposal?: (fieldKey: string, proposalId: number | null) => void;

  // ── E2e-3 §4.3.6: the tri-action on PROJECTED (fact-backed) columns. Keyed by field_key; each entry
  // is one fact behind one rendered chip, already gated per viewer by the server. Absent / empty →
  // nothing renders, which is the whole flag-dark posture (the endpoint returns `{}` when the
  // projection is off), so no affordance can appear before the feature does. ──
  projectedFacts?: Record<string, CrmProjectedChip[]>;
  /**
   * §1.1/§1.2 — the fact operations, now ONE PER FACT rather than per chip.
   *
   * ⚠ The chip-level shape these replace was not an accident of style: the inline controls acted on
   * every fact behind a chip at once, which is right for "reject this company" and wrong for
   * everything else — an entity that is both the target and its own adviser had no way to correct
   * either one separately. The modal edits one fact, so every handler names one.
   */
  onRemoveFact?: (fieldKey: string, fact: CrmProjectedFact, reason: string) => void;
  onConfirmFact?: (fieldKey: string, fact: CrmProjectedFact) => void;
  onRepointFact?: (fieldKey: string, fact: CrmProjectedFact, localId: number) => void;
  onSaveFactRole?: (fieldKey: string, fact: CrmProjectedFact,
                   qualifiers: Record<string, string>) => void;
  /** §1.2: change what KIND of relationship a fact records — a refute + a new predecessor-linked fact. */
  onRetypeFact?: (fieldKey: string, fact: CrmProjectedFact, relationshipType: string,
                  qualifiers: Record<string, string>) => void;
  /** Per-column add inputs: the admissible types and their ordered slots. Absent → no add control. */
  projectedAddOptions?: Record<string, CrmProjectedAddOptions>;
  onAddFact?: (fieldKey: string, relationshipType: string, localId: number,
               qualifiers: Record<string, string>, reason?: string) => void;
  /**
   * §1.3 — read a sentence into a DRAFT fact. The endpoint is READ-ONLY; the write still goes through
   * `onAddFact`, so an accepted parse passes exactly the gates a hand-filled add passes.
   *
   * Absent ⇒ the modal shows the slot controls alone, which is §0's stated fallback ("they become the
   * confirm surface, and the fallback when the model is unavailable") rather than a degraded state.
   */
  onInterpretFact?: (fieldKey: string,
                     args: { text: string; priorProposal?: unknown; answer?: string },
                     onProposal: (r: CrmFactInterpretResponse) => void) => void;
  interpretBusy?: boolean;
  interpretError?: string | null;
  /** Clear any interpret error when the editor closes — a stale red alert must not greet the next one. */
  onFactEditorClosed?: () => void;
  /**
   * Increments when an ADD lands. The editor closes on it.
   *
   * ⚠ Why a signal rather than closing on click: the modal deliberately STAYS OPEN across a write so a
   * refusal is readable and retryable instead of landing behind the backdrop. That works for a chip —
   * the editor is keyed to the entity and closes when its facts are gone — but an add from the
   * field-level `+ Add` has no chip to watch, so nothing ever closed it and a successful add left the
   * dialog sitting there looking like it had failed.
   */
  factAddedNonce?: number;

  // ── D2c: record layout (sectioned field grid) ──
  /** The resolved per-list record layout (sections of field_keys). Absent → flat all-columns render
   * (byte-identical to the pre-D2c panel). A single null-title section renders identically to flat. */
  layout?: CrmRecordLayout;
  /** True → show the ⚙ "Edit layout" affordance in the header (admins only; gated by the container). */
  canEditLayout?: boolean;
  /** Open the (transient) layout editor. */
  onEditLayout?: () => void;

  // ── D5 (R6): deal_pipeline entries — an rtab sub-view switcher + an embedded workflow body ──
  /** E1b-2 — the consolidated timeline, rendered as a SECTION under the field grid (owner ruling
   * 2026-08-21: the timeline is part of the panel, not a tab). Deliberately a SIBLING of
   * `.recordFields` rather than a child: that container is `grid-template-columns: 1fr 1fr`, so a
   * child would become one grid item and the stream would render in a half-width column.
   *
   * It is inside the `record ?` branch, so it does not render — and its lanes do not fetch — while
   * the record is still hydrating or when `bodyOverride` has replaced the body (the admin Updates
   * workflow). */
  timelineSection?: ReactNode;
  /** Sub-view tab bar (Fields / Updates), rendered above the body. Provided by the container ONLY
   * for deal entries (a resolved `deal_ref`); absent → the panel is a plain field grid (unchanged). */
  subViewNav?: ReactNode;
  /** When set, renders INSTEAD of the field grid — the embedded Deal-Updates workflow for the
   * `rtab='updates'` sub-view. Absent → the field grid renders as before (Fields sub-view / non-deal
   * records). The panel stays presentational: the container owns the data + workflow behind this node. */
  bodyOverride?: ReactNode;
  /** notes-provenance: the native Recent-Updates (Progress / New Info) section, rendered INSIDE the
   * Fields body (deal-card style, in-cell). Absent on non-deal records / the sub-views. */
  recentUpdatesSection?: ReactNode;
  /** Item 3b: record-level action bar (Accept / Dismiss / …), rendered in the panel header. Provided
   * by the container ONLY for a proposed record; absent → no bar (committed records are unchanged). */
  recordActions?: ReactNode;
  /** CUT-8: identifies the cell for the per-field history reader. Absent → the affordance is not
   *  offered (the panel still renders everything else). */
  historyIds?: { listId: number | null; entryId: number | null };
}

/**
 * The record overlay. Rendered inside the grid body (position:absolute) so opening/closing never
 * reflows the grid. Escape/close + prev/next are wired here (focus/keyboard); data + mutations come
 * from the container as props.
 */
export function CrmRecordPanel({
  record, columns, position, staleInView, hasPrev, hasNext, onPrev, onNext, onClose, onOpenProfile,
  fieldStates, editMode, options, refOptions, stageLadders, loading, notFound, actionError, busyField,
  onEditField, onRenameDraft, onAcceptProposal, onDismissProposal,
  reviewFields, reviewIsProposed, onDraftEdit, onRejectProposal, onRestoreProposal,
  projectedFacts, onRemoveFact, onConfirmFact, onRepointFact, onSaveFactRole, onRetypeFact,
  projectedAddOptions, onAddFact, onInterpretFact, interpretBusy, interpretError,
  onFactEditorClosed, factAddedNonce, historyIds,
  layout, canEditLayout, onEditLayout,
  subViewNav, bodyOverride, recordActions, recentUpdatesSection, timelineSection,
}: RecordPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  // §15 item 9 — on a whole-record AI proposal EVERY value on the record is proposed, so the rest-state
  // values tone as proposals rather than as settled data. Derived from the record itself through the
  // SAME predicate the grid's full-row-green tint uses, so the panel and the grid can never disagree
  // about whether a record is a proposal. Deliberately NOT `reviewIsProposed`: that prop is present
  // only once record_review.v2 has hydrated, so keying the tone off it would leave a proposed record
  // rendering as committed for as long as the hydrate takes.
  const recordRestTone: ValueTone = isProposedRecordRow(record) ? 'new' : 'plain';
  /**
   * P3 — the org↔people side disagreements on this record, derived once for both renderers.
   *
   * The two catch-all columns are read from the SAME source at any instant — hydrated chips when
   * they have arrived, the scrubbed record cells before that — so the badge is computed from one
   * consistent view and cannot appear on the person before it appears on the firm. Both sources carry
   * the same `role_parties` and `employer` keys, so the answer does not change across the hydrate
   * either; it is the "jumpy display" discipline applied to a derivation instead of a layout.
   *
   * Nothing is fetched. If `all_people` or `all_orgs` is absent (a non-deal record, the projection
   * flag dark, a viewer the gate withheld the org from), the map is empty and no badge renders —
   * which is the correct answer, not a degraded one: with the firm's fact unavailable there is no
   * disagreement to assert.
   */
  const partyContradictions = useMemo(() => {
    const refsOf = (fieldKey: string): PartyRef[] => {
      const chips = projectedFacts?.[fieldKey];
      if (chips && chips.length > 0) return chips.map((c) => c.ref as PartyRef);
      return visibleRefs(record?.[fieldKey]) as PartyRef[];
    };
    return findSideContradictions(
      refsOf(CATCHALL_PEOPLE_FIELD_KEY), refsOf(CATCHALL_ORGS_FIELD_KEY));
  }, [projectedFacts, record]);
  // §1.2 — the fact editor. `entityId: null` is the field-level `+ Add`, which opens the SAME modal
  // with no entity preselected.
  //
  // ⚠ Keyed by the ENTITY, not by a chip object, and that is what keeps the modal honest across a
  // write. The first cut stored the chip and closed the modal on every action, which meant a refusal
  // (400/409) surfaced on the panel BEHIND a dialog that had already thrown away what the operator
  // typed. Holding an id instead lets the modal stay open and re-read the chip from the refetched
  // payload, so `busy` and the error are live rather than decorative, and a failed Save is retryable.
  //
  // `useState`, not a URL param: a transient modal is not deep-linked (`.claude/rules/frontend.md`),
  // and Close must be non-destructive — no pending state survives it.
  const [factEditor, setFactEditor] = useState<
    { fieldKey: string; col: CrmGridColumn; entityId: number | null } | null>(null);
  // An ADD succeeded. Close — there is no chip to re-read, and the operator's next action is a fresh
  // one. Skips the first render so a mounted nonce does not slam the editor shut on open.
  const seenAddNonce = useRef(factAddedNonce);
  useEffect(() => {
    if (factAddedNonce === seenAddNonce.current) return;
    seenAddNonce.current = factAddedNonce;
    setFactEditor(null);
    onFactEditorClosed?.();
  }, [factAddedNonce, onFactEditorClosed]);
  // Close the editor once its entity has no facts left — the operator removed the last one, so the
  // dialog has nothing to show. An empty dialog left open reads as "the removal did not work".
  useEffect(() => {
    if (!factEditor || factEditor.entityId == null) return;
    const chips = projectedFacts?.[factEditor.fieldKey] ?? [];
    if (!chips.some((c) => c.ref.local_id === factEditor.entityId)) {
      setFactEditor(null);
      onFactEditorClosed?.();
    }
  }, [factEditor, projectedFacts, onFactEditorClosed]);
  // Focus into the panel on open, restore to the previously-focused element on close (matches
  // CrmSidePanel). Runs once on mount; a ‹ › conveyor swap keeps the instance mounted so it does NOT
  // steal focus mid-browse.
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => prev?.focus?.();
  }, []);
  // Escape closes the panel, yielding to an open cell editor / a text input first.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // ⚠ A NESTED dialog owns Escape first. Without this, one Escape with the §1.2 fact editor open
      // closed the whole record panel out from under it — the modal survived its own parent. The
      // modal stops propagation, but this listener is on `document` and fires on the capture-less
      // bubble path only when focus is inside the card, which it is not while focus sits on the pencil.
      if (factEditor) return;
      const ae = document.activeElement;
      if (ae && (ae.closest('.ag-cell-inline-editing, .ag-popup-editor')
                 || ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.tagName === 'SELECT')) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, factEditor]);

  const name = cellText(record?.name) || (loading && !record ? '' : '(unnamed)');
  const subjectKind = record?._subject_kind as 'person' | 'organization' | undefined;
  const subjectId = record?._subject_id as number | undefined;
  const kindLabel = subjectKind === 'person' ? 'Person' : subjectKind === 'organization' ? 'Organization' : 'Record';
  // every column except the identity `name` (shown in the header)
  const fieldCols = useMemo(() => columns.filter((c) => c.field_key !== 'name'), [columns]);
  const actionable = !!fieldStates; // hydrate-before-actionable (§6.5)
  // Editable title (owner requests 2026-08-18 — a promoted deal shipped "(unnamed)" with no way to
  // fix it, then "candidates also should have editable names"). Opportunity records ONLY: on
  // person/org lists the directory record owns the name. The layout deliberately keeps `name` out of
  // the field grid (it IS the header), so the header is where the edit affordance belongs. TWO write
  // paths behind one pencil: a COMMITTED record's name is an ordinary cell edit (onEditField); a
  // PROPOSED draft's name is CANDIDATE data — set_field_value refuses drafts by the D8 rule — so it
  // takes the dedicated rename-draft route (onRenameDraft).
  const isProposedRecord = !!record && '_proposed_record' in record;
  const canEditName = !subjectKind && !!record
    && (isProposedRecord ? !!onRenameDraft : (actionable && !!onEditField));
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const saveName = () => {
    const v = nameDraft.trim();
    if (v && v !== cellText(record?.name)) {
      if (isProposedRecord) onRenameDraft?.(v);
      else onEditField?.('name', v);
    }
    setEditingName(false);
  };

  // D2c: group the fields into sections per the resolved layout. No layout (or a single null-title
  // section) → one untitled section = the flat all-columns render (byte-identical to pre-D2c). A
  // field the layout omits is appended in a trailing section (defensive — the backend already
  // guarantees this) so a column is never invisible.
  /**
   * Is this field hidden from the panel? (Layout hiding itself: the record-panel UX memo §15 item 6.
   * The reveal rule below, and the 2026-08-10 owner ruling that narrowed it, are recorded in
   * `docs/specs/strategic-architecture/crm-team-default-view-spec.md` §7.)
   *
   * ⚠ **A hidden field AUTO-REVEALS for exactly three things, all of them PENDING DECISIONS: a live
   * AI proposal, a rejected one an operator could restore, and a failed push.** Hiding is a
   * decluttering tool, and without this it would also be a way to make work invisible AND
   * un-actionable: an operator hides `NDA Term`, the aggregator proposes a value for it, and the
   * proposal can never be seen or decided — silently, with the pending count still counting it. That
   * reveal is the whole reason hiding is safe to offer at all.
   *
   * ⛔ **PROJECTED FACTS ARE NOT ONE OF THEM — do not reintroduce a `projectedFacts` clause either**
   * (owner ruling 2026-08-10). Published facts are CONTENT, not a decision waiting on anybody: the
   * pending count never counts them, correcting one is optional, and the fact stays reachable from the
   * record's fact surfaces and the entity profiles regardless of this panel. So burying one buries
   * nothing, and the safety rule above simply does not apply. Keeping the clause is what made hiding
   * a no-op for most of the fields the owner actually hid — Seller Orgs, Organizations, People,
   * Advisors and FA are all projection-registry keys, and the projection is ON in prod.
   *
   * ⛔ **The reveal keys on a pending DECISION, never on "this field is editable" — do not
   * reintroduce an `allowed_actions.length > 0` clause.** It looks equivalent and is not: the
   * backend hands EVERY field of an editable list a non-empty action list — `record_review.py`
   * `_field_actions` returns the proposed-row set for every field of a PROPOSED row, and a plain
   * committed field with no proposal is assembled with `allowed_actions: ['edit']`. So that clause
   * fired on all 14 layout-hidden IOT fields (the IOT is editable) and hiding was a silent no-op.
   * `proposal_id != null` is the correct test for the restorable case too — a rejected/retracted
   * field is still a proposal member and keeps its `proposal_id` (`record_review.py`: every member
   * field is built with `"proposal_id": int(prop["id"])`), so `restore` stays reachable without it.
   */
  const isHidden = (fieldKey: string): boolean => {
    if (!layout?.hidden_field_keys?.includes(fieldKey)) return false;
    const st = fieldStates?.[fieldKey];
    if (st && (st.status === 'proposed' || st.status === 'push_failed')) return false;
    const rf = reviewFields?.[fieldKey];
    // ⚠ On a COMMITTED row the disposition test is LOAD-BEARING, not defensive. `proposal_id != null`
    // alone was sufficient only while the container admitted pending members only; since D4 it admits
    // RESOLVED members too, and in prod most committed fields carry a settled proposal (270 superseded
    // + 64 accepted on the IOT). Without this, accepting a proposal on a hidden field would un-hide it
    // forever — the same "hiding is a silent no-op" bug the owner ruling of 2026-08-10 fixed. A
    // pending DECISION reveals: a live proposal, or a rejected/retracted one an operator could restore.
    // Proposed rows keep the whole-row rule (every member reveals) exactly as before.
    if (rf && rf.proposal_id != null
        && (reviewIsProposed || rf.disposition === 'pending'
            || rf.disposition === 'rejected' || rf.disposition === 'retracted')) return false;
    return true;
  };

  // D2c: group the fields into sections per the resolved layout. No layout (or a single null-title
  // section) → one untitled section = the flat all-columns render (byte-identical to pre-D2c). A
  // field the layout omits is appended in a trailing section (defensive — the backend already
  // guarantees this) so a column is never invisible.
  const sections = useMemo<{ title: string | null; cols: CrmGridColumn[]; columns: number }[]>(() => {
    // D-5 (owner, 2026-09-05): *`parties` replaces the two catch-alls IN THE PANEL; the grid keeps the
    // flat columns.* Keyed on the `parties` COLUMN being present in this payload rather than on the
    // flag, so the swap follows the server's own column set — the flag governs whether the column is
    // served at all (`lists._field_on_surface`), and reading it twice is how a panel comes to hide a
    // field whose replacement was never sent. With `parties` absent (flag dark, or a list that does
    // not carry it) this is a no-op and the panel is byte-identical to pre-D2.
    const hasParties = fieldCols.some((c) => c.field_key === PARTIES_FIELD_KEY
                                        && c.field_type === PARTY_TREE_FIELD_TYPE);
    const visible = fieldCols.filter((c) => !isHidden(c.field_key)
      && !(hasParties && CATCHALL_FIELD_KEYS.has(c.field_key)));
    const byKey = new Map(visible.map((c) => [c.field_key, c]));
    const secs = layout?.sections;
    if (!secs || secs.length === 0) return [{ title: null, cols: visible, columns: 2 }];
    const placed = new Set<string>();
    const out: { title: string | null; cols: CrmGridColumn[]; columns: number }[] = [];
    for (const sec of secs) {
      const cols: CrmGridColumn[] = [];
      for (const fk of sec.field_keys) {
        const col = byKey.get(fk);
        if (col && !placed.has(fk)) { cols.push(col); placed.add(fk); }
      }
      // A section whose every field is hidden renders nothing — including its title, which would
      // otherwise be a heading over empty space.
      if (cols.length === 0 && sec.title !== null) continue;
      out.push({ title: sec.title ?? null, cols, columns: sec.columns === 1 ? 1 : 2 });
    }
    const leftover = visible.filter((c) => !placed.has(c.field_key));
    if (leftover.length > 0) out.push({ title: null, cols: leftover, columns: 2 });
    return out;
    // projectedFacts is deliberately NOT a dep any more: isHidden no longer reads it, so a fact
    // arriving or being corrected cannot change which sections exist.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldCols, layout, fieldStates, reviewFields, reviewIsProposed]);

  // plan 1b: the record's own field values, read the way the rows below read theirs — the field
  // STATE first, because an overlay-pending edit is what this record now says, and the ladder must
  // follow the value the operator can see rather than the last committed one.
  const effectiveValue = (key: string): unknown => {
    const fs = fieldStates?.[key];
    return fs ? fs.effective_value : record?.[key];
  };
  const recordDealType = stageLadders ? effectiveValue(stageLadders.deal_type_field_key) : undefined;
  const renderFieldRow = (c: CrmGridColumn) => {
    // plan 1b: for the Stage field, the ladder this record's `deal_type` calls for; for every other
    // field (and every list with no ladder block) this is the untouched picklist. Filtered BEFORE
    // `panelFieldEditor` so the editor choice follows the ladder actually offered — a 9-option exit
    // ladder gets a plain <select>, not the >20 searchable checklist the full list would earn.
    const opts = stageLadderOptions(c.field_key, options?.[c.field_key],
                                    effectiveValue(c.field_key), recordDealType, stageLadders);
    // PR3 item 7: the eligible people for a roster-flagged ref column (`deal_team` / `deal_lead`).
    // Undefined for every other column, which is what keeps them on the directory typeahead.
    const refPeople = refOptions?.[c.field_key];
    // E2e-3: a fact-backed column is corrected through the ledger, not through the EAV — so it takes
    // precedence over BOTH the v1 and v2 field rows, which would offer an ordinary cell edit that
    // writes the wrong layer (§4.3.6: "a direct cell edit is rejected with a pointer to the panel").
    // Gated on facts existing rather than on `status === 'projected'`: the kernel deliberately ranks
    // `projected` below every human action, so once someone has acted the status is theirs — and
    // hiding the correction path exactly then is backwards.
    const chips = projectedFacts?.[c.field_key];
    // ⚠ Gated on EVERY write the modal offers, not just one. The modal's handlers are optional-chained,
    // so a missing one made Save close the dialog and do nothing at all — silently. Requiring them here
    // means the pencil is never shown for an editor that cannot actually edit.
    if (actionable && chips && chips.length > 0
        && onRemoveFact && onConfirmFact && onRepointFact && onSaveFactRole && onRetypeFact) {
      return (
        <ProjectedFieldRow
          key={c.field_key}
          col={c}
          chips={chips}
          busy={busyField === c.field_key}
          addOptions={projectedAddOptions?.[c.field_key]}
          onOpenEditor={(chip) => setFactEditor(
            { fieldKey: c.field_key, col: c, entityId: chip.ref.local_id })}
          onOpenAdd={onAddFact
            ? () => setFactEditor({ fieldKey: c.field_key, col: c, entityId: null }) : undefined}
        />
      );
    }
    // R5/R4-1: a row hydrated from record_review.v2 (PROPOSED or COMMITTED) renders its per-field review
    // via ReviewFieldRow (allowed_actions-driven). Absent reviewFields (flag dark) → the v1 path.
    const reviewField = reviewFields?.[c.field_key];
    if (reviewField) {
      const { editable, kind } = panelFieldEditor(c, editMode, opts, refPeople);
      return (
        <ReviewFieldRow
          key={c.field_key}
          col={c}
          field={reviewField}
          editable={editable}
          editorKind={kind}
          options={opts}
          refPeople={refPeople}
          busy={busyField === c.field_key}
          isProposedRow={!!reviewIsProposed}
          restTone={recordRestTone}
          historyIds={historyIds}
          record={record}
          onDraftEdit={onDraftEdit ? (v) => onDraftEdit(c.field_key, v) : undefined}
          onReject={onRejectProposal ? (pid) => onRejectProposal(c.field_key, pid) : undefined}
          onRestore={onRestoreProposal ? (pid) => onRestoreProposal(c.field_key, pid) : undefined}
        />
      );
    }
    const st = fieldStates?.[c.field_key];
    const val = effectiveValue(c.field_key);
    const { editable, kind } = actionable
      ? panelFieldEditor(c, editMode, opts, refPeople)
      : { editable: false, kind: null };
    return (
      <RecordFieldRow
        key={c.field_key}
        col={c}
        historyIds={historyIds}
        value={val}
        state={st}
        editable={editable}
        editorKind={kind}
        options={opts}
        refPeople={refPeople}
        restTone={recordRestTone}
        record={record}
        busy={busyField === c.field_key}
        onEdit={actionable && onEditField ? (v) => onEditField(c.field_key, v) : undefined}
        onAccept={actionable && onAcceptProposal ? (pid) => onAcceptProposal(c.field_key, pid) : undefined}
        onDismiss={actionable && onDismissProposal ? (pid) => onDismissProposal(c.field_key, pid) : undefined}
      />
    );
  };

  return (
    <PartyContradictionsContext.Provider value={partyContradictions}>
    {/* `data-crm-record-panel` (F.8) marks this dialog as NOT a modal for keyboard-nav purposes, so
        ←/→ record navigation fires while the panel holds focus. It must stay SEPARATE from
        `data-crm-panel-keepopen`, whose contract covers surfaces this panel spawns too — see
        `crmRecordKeyNav.RECORD_PANEL_ATTR`, which `crmRecordNav.test.tsx` pins to this literal. */}
    <div ref={panelRef} className={styles.recordPanel} role="dialog" data-crm-panel-keepopen data-crm-record-panel aria-label={`Record: ${name || 'loading'}`} tabIndex={-1}>
      <div className={styles.recordPanelHead}>
        {/* §15 item 8 — Close leads the header, then the ‹ › nav. Closing is the action an operator
            reaches for most and by far the least reversible-by-accident; it belongs where the eye
            starts, not tucked behind the layout gear on the far right. */}
        <div className={styles.recordPanelNav}>
          <button className={styles.recordPanelClose} onClick={onClose} aria-label="Close record">✕</button>
          <button className={styles.recordNavBtn} onClick={onPrev} disabled={!hasPrev} aria-label="Previous record" title="Previous record (←)">‹</button>
          <button className={styles.recordNavBtn} onClick={onNext} disabled={!hasNext} aria-label="Next record" title="Next record (→)">›</button>
          {position && <span className={styles.recordNavPos}>{position.index} of {position.total}</span>}
        </div>
        {/* Item 3b: record-level actions (proposed rows) sit beside the ‹ › nav. */}
        {recordActions}
        <div className={styles.recordPanelHeadActions}>
          {canEditLayout && onEditLayout && (
            <button className={styles.recordLayoutBtn} onClick={onEditLayout}
                    aria-label="Configure record layout" title="Edit layout (admin)">⚙</button>
          )}
        </div>
      </div>

      {notFound ? (
        <div className={styles.recordEmptyState} role="status">
          <div className={styles.recordEmptyTitle}>Record not available</div>
          <div className={styles.recordEmptyHint}>It may have been removed, or you don't have access to it.</div>
        </div>
      ) : (
        <>
          <div className={styles.recordPanelTitleRow}>
            <span className={styles.recordPanelKind}>{kindLabel}</span>
            {editingName && canEditName ? (
              <input
                className={styles.recordPanelNameInput}
                value={nameDraft}
                autoFocus
                aria-label="Record name"
                onChange={(e) => setNameDraft(e.target.value)}
                onBlur={saveName}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveName();
                  if (e.key === 'Escape') {
                    e.stopPropagation();
                    setEditingName(false);
                  }
                }}
              />
            ) : (
              <h2 className={styles.recordPanelName}>{name || (loading ? 'Loading…' : '(unnamed)')}</h2>
            )}
            {canEditName && !editingName && (
              <button
                className={styles.recordNameEditBtn}
                onClick={() => {
                  setNameDraft(cellText(record?.name) || '');
                  setEditingName(true);
                }}
                aria-label="Edit record name"
                title="Edit name"
              >
                ✎
              </button>
            )}
          </div>

          {staleInView && (
            <div className={styles.recordStaleBanner} role="status">
              This record is no longer in the current view — use ‹ › to jump to the nearest record.
            </div>
          )}
          {actionError && (
            <div className={styles.recordActionError} role="alert">{actionError}</div>
          )}

          {subjectKind && subjectId != null && onOpenProfile && (
            <button className={styles.recordProfileLink} onClick={() => onOpenProfile(subjectKind, subjectId)}>
              Open full profile ↗
            </button>
          )}

          {/* D5: the Fields / Updates sub-view switcher (deal entries only). */}
          {subViewNav}

          {/* D5: the Updates sub-view replaces the field grid entirely (the container passes the
              embedded Deal-Updates workflow as bodyOverride). Everything else keeps the field grid. */}
          {bodyOverride != null ? (
            bodyOverride
          ) : record ? (
            <>
              <div className={styles.recordFields}>
                {sections.map((sec, si) => (
                  <Fragment key={sec.title != null ? `s:${sec.title}:${si}` : `flat:${si}`}>
                    {sec.title != null && (
                      <div className={styles.recordSectionHeader}>{sec.title}</div>
                    )}
                    {/* §15 item 6. ⚠ `.recordFields` is ALREADY `grid-template-columns: 1fr 1fr`, so
                        two-up is the panel's natural flow and needs no wrapper — TWO is the default.
                        A ONE-column section is the special case: its rows must span both tracks.
                        The wrapper is `display: contents` so the rows stay grid items of `.recordFields`;
                        an ordinary wrapper div would become a single grid item and nest a grid inside one
                        column, which is exactly how the first cut of this broke. */}
                    {sec.columns === 1 ? (
                      <div className={styles.recordSectionOneCol}>{sec.cols.map(renderFieldRow)}</div>
                    ) : (
                      sec.cols.map(renderFieldRow)
                    )}
                  </Fragment>
                ))}
                {/* notes-provenance: Progress / New Info, native to the Fields body (deal-card style). */}
                {recentUpdatesSection}
              </div>
              {timelineSection}
            </>
          ) : (
            <div className={styles.recordFields}>
              <div className={styles.recordSkeletonNote}>Loading record…</div>
            </div>
          )}

        </>
      )}

      {/* §1.2 — the one surface for every operation on this row's facts. Rendered from the panel rather
          than the row because the governed ref picker lives here, and it is injected into the modal as a
          render prop: `PopupEditor` mounts `RefCellEditor`, which queries the directory and needs a
          QueryClient, so keeping it out of the modal's own imports is what makes the modal testable
          without a provider — the documented gap where "the panel test suite cannot open the add picker". */}
      {factEditor && (() => {
        // Re-read from the CURRENT payload each render, so a successful write refreshes the modal in
        // place instead of showing the snapshot it opened with.
        const openChips = projectedFacts?.[factEditor.fieldKey] ?? [];
        const openChip = factEditor.entityId == null ? null
          : openChips.find((c) => c.ref.local_id === factEditor.entityId) ?? null;
        // The entity's last fact was removed, so there is nothing left to edit. Render nothing; the
        // effect above clears the state (setState during render is not allowed).
        if (factEditor.entityId != null && openChip === null) return null;
        return (
          <CrmFactEditModal
            columnLabel={factEditor.col.label}
            entryLabel={cellText(record?.name) || 'this record'}
            chip={openChip}
            addOptions={projectedAddOptions?.[factEditor.fieldKey]}
            busy={busyField === factEditor.fieldKey}
            error={actionError ?? null}
            onClose={() => { setFactEditor(null); onFactEditorClosed?.(); }}
            // `allowCreate` is passed HERE and nowhere else. This is the point of need: the fact-add
            // names the entity last, through the governed picker, and the picker can only offer a
            // company the CRM has learned about from mail — so a target that has never emailed us
            // (Orange Flood Control, on a deal in exclusivity) was a dead end. The create affordance
            // lives inside the picker, not in the modal, because the modal takes the picker as a
            // render prop precisely so it owns no write path.
            renderRefPicker={({ initial, onCommit, onCancel }) => (
              <PopupEditor kind="ref" col={factEditor.col} initial={initial} options={undefined}
                           singleRef allowCreate onCommit={onCommit} onCancel={onCancel} />
            )}
            // WP-6b — the governed ORGANISATION picker for `acting_for`, and it is a SEPARATE prop
            // because the column's own kind is wrong for it: on `linked_people` the picker above
            // offers people, and "which firm did they act through" is never a person. The column is
            // spread and its `field_type` overridden rather than a synthetic one invented, so the
            // editor keeps whatever the column already configures. `allowCreate` is deliberately NOT
            // passed: a capacity names a firm the CRM has met, and minting one from a correction
            // dialog is how a typo becomes a directory entry.
            renderOrgPicker={({ initial, onCommit, onCancel }) => (
              <PopupEditor kind="ref"
                           col={{ ...factEditor.col, field_type: 'organization',
                                  label: 'Acting for' }}
                           initial={initial} options={undefined}
                           singleRef onCommit={onCommit} onCancel={onCancel} />
            )}
            // ⚠ The modal STAYS OPEN across a write. Every one of these is a ledger command that can be
            // refused (400 on the ledger's own terms, 409 on a stale CAS), and closing first put that
            // refusal on the panel behind the backdrop with the operator's input already discarded.
            // Staying open keeps `busy` and `error` meaningful and the action retryable; the refetch
            // above is what stops it showing stale values after a success.
            onSaveFact={(f, q) => onSaveFactRole?.(factEditor.fieldKey, f, q)}
            onRetypeFact={(f, t, q) => onRetypeFact?.(factEditor.fieldKey, f, t, q)}
            onChangeEntity={(f, id) => onRepointFact?.(factEditor.fieldKey, f, id)}
            onRemoveFact={(f, reason) => onRemoveFact?.(factEditor.fieldKey, f, reason)}
            onConfirmFact={(f) => onConfirmFact?.(factEditor.fieldKey, f)}
            onAddFact={(t, id, q, reason) =>
              onAddFact?.(factEditor.fieldKey, t, id, q, reason)}
            describe={onInterpretFact ? {
              run: (args, onProposal) =>
                onInterpretFact(factEditor.fieldKey, args, onProposal),
              busy: interpretBusy, error: interpretError ?? null,
            } : undefined}
          />
        );
      })()}
    </div>
    </PartyContradictionsContext.Provider>
  );
}
